<template>
  <div>
    <b-modal
      id="simple-table-builder"
      centered
      size="xl"
      :modal-class="isFullscreen ? 'modal-1200 table-builder-fullscreen' : 'modal-1200'"
      @ok="onSave"
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title table-builder-title">
          Table Builder
        </h5>
        <div class="d-flex align-items-center ml-auto">
          <button
            type="button"
            class="builder-btn builder-btn--ghost builder-btn--icon"
            :title="isFullscreen ? 'Exit full screen' : 'Full screen'"
            @click="isFullscreen = !isFullscreen"
          >
            <LucideIcon
              :name="isFullscreen ? 'minimize-2' : 'maximize-2'"
              :size="16"
            />
          </button>
          <div class="builder-divider mx-2" />
          <button
            type="button"
            class="builder-btn builder-btn--ghost builder-btn--icon"
            aria-label="Close"
            @click="close()"
          >
            <LucideIcon
              name="x"
              :size="16"
            />
          </button>
        </div>
      </template>

      <div
        v-if="table && table.rows.length"
        class="mb-2"
      >
        <div class="table-toolbar table-toolbar--main d-flex flex-wrap align-items-center">
          <b-dropdown
            variant="link"
            no-caret
            toggle-class="builder-btn"
          >
            <template #button-content>
              Static Cells
              <LucideIcon
                name="chevron-down"
                :size="14"
              />
            </template>
            <b-dropdown-item @click="addStaticRowAbove">
              Add Static Row above the table
            </b-dropdown-item>
            <b-dropdown-item @click="addStaticRowBelow">
              Add Static Row below the table
            </b-dropdown-item>
            <b-dropdown-item
              :disabled="!canAddStaticRowAtSelection"
              title="Select a cell of the row you want it above"
              @click="addStaticRowAboveSelected"
            >
              Add Static Row above the selected row
            </b-dropdown-item>
            <b-dropdown-item
              :disabled="!canAddStaticRowAtSelection"
              title="Select a cell of the row you want it below"
              @click="addStaticRowBelowSelected"
            >
              Add Static Row below the selected row
            </b-dropdown-item>
            <b-dropdown-item
              :disabled="!canAddStaticCol"
              title="Select a static cell that covers more than one column"
              @click="addStaticCol"
            >
              Add Static Col
            </b-dropdown-item>
          </b-dropdown>

          <div class="builder-divider" />

          <button
            type="button"
            class="builder-btn builder-btn--ghost"
            title="Add Row"
            @click="addRow"
          >
            <LucideIcon
              name="plus"
              :size="16"
            />
            Row
          </button>
          <button
            type="button"
            class="builder-btn builder-btn--ghost"
            title="Add Column"
            @click="addColumn"
          >
            <LucideIcon
              name="plus"
              :size="16"
            />
            Column
          </button>
          <button
            type="button"
            class="builder-btn builder-btn--ghost"
            title="Remove Row"
            :disabled="!canRemoveRow"
            @click="removeRow"
          >
            <LucideIcon
              name="minus"
              :size="16"
            />
            Row
          </button>
          <button
            type="button"
            class="builder-btn builder-btn--ghost"
            title="Remove Column"
            :disabled="!canRemoveColumn"
            @click="removeColumn"
          >
            <LucideIcon
              name="minus"
              :size="16"
            />
            Column
          </button>

          <div class="builder-divider" />

          <DropdownCounter
            :value="getStyleValue('font-size', 0)"
            :min="1"
            :step="1"
            variant="link"
            no-caret
            toggle-class="builder-btn builder-btn--ghost builder-btn--icon"
            @input="(value) => onChangeStyleOfTable('font-size', value, 'px')"
          >
            <LucideIcon
              name="type"
              :size="16"
            />
          </DropdownCounter>
          <DropdownCounter
            :value="getStyleValue('border-width', 0)"
            :min="0"
            :step="1"
            variant="link"
            no-caret
            toggle-class="builder-btn builder-btn--ghost builder-btn--icon"
            @input="(value) => onChangeStyleOfTable('border-width', value, 'px', true)"
          >
            <LucideIcon
              name="square"
              :size="16"
            />
          </DropdownCounter>
          <div class="table-toolbar__swatch">
            <ColorPicker
              :value="getStyleValue('color', '#000000')"
              @input="(value) => onChangeStyleOfTable('color', value)"
            />
          </div>

          <div class="d-flex align-items-center ml-auto">
            <label
              class="builder-label mb-0 mr-2"
              for="table-width"
            >
              W
            </label>
            <b-input
              id="table-width"
              :value="tableWidth"
              type="number"
              class="table-toolbar__size-input"
              @input="(value) => changeTableWidth(value)"
            />
            <label
              class="builder-label mb-0 ml-3 mr-2"
              for="table-height"
            >
              H
            </label>
            <b-input
              id="table-height"
              :value="tableHeight"
              type="number"
              class="table-toolbar__size-input"
              :disabled="autoHeight"
              @input="(value) => changeTableHeight(value)"
            />
            <b-form-checkbox
              v-model="borderBoxWidths"
              switch
              class="table-toolbar__switch ml-3"
              title="A cell's Fixed Width counts its paddings and borders, so a column set to 250 takes 250 on the page"
              @change="onChangeBorderBoxWidths"
            >
              Width includes paddings
            </b-form-checkbox>
          </div>
        </div>
        <div class="table-toolbar d-flex flex-wrap align-items-center mb-3">
          <button
            type="button"
            class="builder-btn"
            title="Insert Row Above"
            :disabled="!canInsertRowAbove"
            @click="insertRowAbove"
          >
            <LucideIcon
              name="plus"
              :size="14"
            />
            Row Above
          </button>
          <button
            type="button"
            class="builder-btn"
            title="Insert Row Below"
            :disabled="!canInsertRowBelow"
            @click="insertRowBelow"
          >
            <LucideIcon
              name="plus"
              :size="14"
            />
            Row Below
          </button>
          <button
            type="button"
            class="builder-btn"
            title="Insert Column Left"
            :disabled="!canInsertColumnLeft"
            @click="insertColumnLeft"
          >
            <LucideIcon
              name="plus"
              :size="14"
            />
            Column Left
          </button>
          <button
            type="button"
            class="builder-btn"
            title="Insert Column Right"
            :disabled="!canInsertColumnRight"
            @click="insertColumnRight"
          >
            <LucideIcon
              name="plus"
              :size="14"
            />
            Column Right
          </button>

          <div class="builder-divider" />

          <button
            type="button"
            class="builder-btn"
            title="Move Row Up"
            :disabled="!canMoveRowUp"
            @click="moveRowUp"
          >
            Move ↑
          </button>
          <button
            type="button"
            class="builder-btn"
            title="Move Row Down"
            :disabled="!canMoveRowDown"
            @click="moveRowDown"
          >
            Move ↓
          </button>
          <button
            type="button"
            class="builder-btn"
            title="Move Column Left"
            :disabled="!canMoveColumnLeft"
            @click="moveColumnLeft"
          >
            Move ←
          </button>
          <button
            type="button"
            class="builder-btn"
            title="Move Column Right"
            :disabled="!canMoveColumnRight"
            @click="moveColumnRight"
          >
            Move →
          </button>

          <div class="d-inline-flex align-items-center justify-content-center table-zoom-control ml-auto">
            <button
              type="button"
              class="table-zoom-control__btn"
              title="Zoom out"
              aria-label="Zoom out"
              :disabled="zoomLevel <= 25"
              @click="zoomOut"
            >
              <LucideIcon
                name="zoom-out"
                :size="14"
              />
            </button>
            <div class="table-zoom-control__value">
              {{ zoomLevel }}%
            </div>
            <button
              type="button"
              class="table-zoom-control__btn"
              title="Zoom in"
              aria-label="Zoom in"
              :disabled="zoomLevel >= 100"
              @click="zoomIn"
            >
              <LucideIcon
                name="zoom-in"
                :size="14"
              />
            </button>
          </div>

          <b-dropdown
            variant="link"
            no-caret
            right
            toggle-class="builder-btn"
            menu-class="table-options-menu"
          >
            <template #button-content>
              Table Options
              <LucideIcon
                name="chevron-down"
                :size="14"
              />
            </template>
            <b-dropdown-form>
              <b-form-checkbox
                v-model="autoHeight"
                class="mb-2"
                @change="handleAutoHeightChange"
              >
                Auto-height
              </b-form-checkbox>

              <b-form-checkbox
                v-model="autoFontSize"
                class="mb-2"
                @change="handleAdjustFontSizeToTable"
              >
                Adjust font to table size
              </b-form-checkbox>

              <b-form-checkbox
                v-model="tableColumnMode"
                class="mb-2"
                @change="onChangeTableColumnMode"
              >
                Column Mode
              </b-form-checkbox>
              <b-form-checkbox
                v-model="hideHeadersToggle"
                class="mb-2"
                @change="onChangeHideHeaders"
              >
                Hide headers
              </b-form-checkbox>
              <b-form-checkbox
                v-model="mergeSameValues"
                class="mb-2"
              >
                Merge cells with same values
              </b-form-checkbox>
              <b-form-checkbox
                v-model="truncateLongText"
                title="Show ellipsis when text exceeds the cell width"
              >
                Truncate long text
              </b-form-checkbox>
            </b-dropdown-form>
          </b-dropdown>
        </div>

        <div
          v-if="selectedCells.length"
          class="table-cell-panel d-flex"
        >
          <div class="table-cell-panel__side d-flex flex-column">
            <Multiselect
              v-if="themeStyles"
              v-model="selectedStyle"
              placeholder="Select style"
              track-by="name"
              label="name"
              class="table-multiselect"
              select-label=" "
              deselect-label=" "
              :options="themeStyles"
              :allow-empty="false"
              @input="applyTextStyle"
            >
              <template #option="props">
                <span :style="getStylePreview(props.option)">{{ props.option.name }}</span>
              </template>
              <template #singleLabel="props">
                <span :style="getStylePreview(props.option)">{{ props.option.name }}</span>
              </template>
            </Multiselect>

            <div class="d-flex align-items-center">
              <label
                class="table-cell-panel__name-label mb-0"
                for="cell-name"
              >
                Name
              </label>

              <InfoHint
                class="ml-auto mr-2"
                text="Select text and click a formatting button to apply or remove formatting"
              />

              <div
                class="format-hover-dropdown"
                @mouseenter="showFormatMenu = true"
                @mouseleave="showFormatMenu = false"
              >
                <button
                  type="button"
                  class="builder-btn builder-btn--primary"
                >
                  Formatting
                </button>

                <div
                  v-show="showFormatMenu"
                  class="format-hover-dropdown__menu"
                  @mousedown.prevent
                >
                  <button
                    type="button"
                    class="format-hover-dropdown__item"
                    :class="{ active: isTagActive('b') }"
                    @click.prevent="applyBoldTag"
                  >
                    Bold
                  </button>

                  <button
                    type="button"
                    class="format-hover-dropdown__item"
                    @click.prevent="insertNbsp"
                  >
                    Insert nbsp
                  </button>

                  <button
                    type="button"
                    class="format-hover-dropdown__item"
                    :class="{ active: isTagActive('span') }"
                    @click.prevent="insertFont"
                  >
                    Font
                  </button>

                  <button
                    type="button"
                    class="format-hover-dropdown__item"
                    :class="{ active: isTagActive('sup') }"
                    @click.prevent="applySupTag"
                  >
                    Superscript
                  </button>

                  <button
                    type="button"
                    class="format-hover-dropdown__item"
                    :class="{ active: isTagActive('sub') }"
                    @click.prevent="applySubTag"
                  >
                    Subscript
                  </button>
                </div>
              </div>
            </div>

            <b-form-textarea
              id="cell-name"
              ref="cellValueTa"
              :value="selectedCells[0].valueHtml"
              type="text"
              rows="2"
              @input="(value) => selectedCells[0].valueHtml = value"
            />

            <div class="d-flex align-items-center">
              <b-input
                id="link"
                :value="selectedCells[0].link"
                type="text"
                placeholder="Link"
                @input="(value) => selectedCells[0].link = value"
              />
              <InfoHint
                class="ml-1"
                text="Used on PDF exports to create links"
              />
            </div>

            <div class="d-flex align-items-center">
              <b-input
                id="mapping-name"
                :value="selectedCells[0].mappingName"
                type="text"
                placeholder="Mapping name"
                @input="onMappingNameInput"
                @change="refreshTableData"
              />
              <InfoHint
                class="ml-1"
                text="Import fills this cell on its own, by this name. Leave empty to keep the cell as it is."
              />
              <button
                type="button"
                class="builder-btn builder-btn--icon table-cell-panel__mapping-btn ml-1"
                :disabled="!labelForSelectedCell"
                :title="labelForSelectedCell ? `Use \u201c${labelForSelectedCell}\u201d` : 'No label above or to the left'"
                @click="useLabelAsMappingName"
              >
                <LucideIcon
                  name="upload"
                  :size="16"
                />
              </button>
            </div>
          </div>

          <div class="table-cell-panel__controls">
            <div class="table-cell-panel__row d-flex align-items-end">
              <div>
                <label
                  class="builder-label"
                  for="font-family"
                >
                  Font Family
                </label>
                <select
                  id="font-family"
                  class="custom-select custom-select-sm table-cell-panel__font-family"
                  :value="fontFamily"
                  @input="setFontFamily"
                >
                  <option
                    v-for="(currentFont, index) in fontsList"
                    :key="(currentFont.index || currentFont.created_at) + index"
                    :value="currentFont.index || currentFont"
                    :style="{'font-family': currentFont.index}"
                  >
                    {{ currentFont.name || currentFont.title }}
                  </option>
                </select>
              </div>

              <div>
                <label
                  class="builder-label"
                  for="text-aligned"
                >
                  Aligned
                </label>
                <select
                  id="text-aligned"
                  class="custom-select custom-select-sm table-cell-panel__align"
                  :value="align"
                  @input="setFontAligned"
                >
                  <option
                    v-for="(item, index) in textAlignedList"
                    :key="item.value + index"
                    :value="item.value"
                  >
                    {{ item.text }}
                  </option>
                </select>
              </div>

              <div>
                <label
                  class="builder-label"
                  for="cell-font-size"
                >
                  Font Size
                </label>
                <b-input
                  id="cell-font-size"
                  v-model="selectedCellTextSize"
                  type="number"
                  size="sm"
                  class="table-cell-panel__font-size"
                  @input="(value) => handleChangeCellStyleRule('font-size', value, 'px')"
                />
              </div>
            </div>

            <div class="table-cell-panel__row d-flex align-items-end mt-3">
              <div>
                <label
                  class="builder-label"
                  for="cell-width"
                >
                  Fixed Width
                </label>
                <b-input
                  id="cell-width"
                  v-model="selectedCellWidth"
                  type="number"
                  size="sm"
                  class="table-cell-panel__number"
                  @input="(value) => handleChangeColumnWidth('width', value)"
                />
              </div>

              <div>
                <label
                  class="builder-label"
                  for="cell-min-width"
                >
                  Min Width
                </label>
                <b-input
                  id="cell-min-width"
                  v-model="selectedCellMinWidth"
                  type="number"
                  size="sm"
                  class="table-cell-panel__number"
                  @input="(value) => handleChangeColumnWidth('min-width', value)"
                />
              </div>

              <div>
                <label
                  class="builder-label"
                  for="cell-height"
                >
                  Height
                </label>
                <b-input
                  id="cell-height"
                  v-model="selectedCellMinHeight"
                  type="number"
                  size="sm"
                  class="table-cell-panel__number"
                  @input="(value) => handleChangeCellHeight(value)"
                />
              </div>

              <div>
                <label
                  class="builder-label"
                  for="cell-angle"
                >
                  Angle
                </label>
                <div class="d-flex">
                  <b-input
                    id="cell-angle"
                    v-model="selectedCellAngle"
                    type="number"
                    size="sm"
                    class="table-cell-panel__angle"
                    @input="(value) => handleChangeCellAngle(value)"
                  />
                  <button
                    type="button"
                    class="builder-btn table-cell-panel__fit ml-1"
                    title="Fit cell to the rotated text"
                    :disabled="!hasCellAngle"
                    @click="fitCellsToAngle"
                  >
                    Fit
                  </button>
                </div>
              </div>

              <div>
                <label
                  class="builder-label"
                  for="cell-paddings"
                >
                  Paddings
                </label>
                <b-input
                  id="cell-paddings"
                  v-model="selectedPaddings"
                  type="text"
                  size="sm"
                  class="table-cell-panel__number"
                  @input="(value) => changeCellPaddings(value)"
                />
              </div>

              <b-form-checkbox
                v-model="selectedCellDotLeaderEnabled"
                switch
                class="table-cell-panel__dots"
                @change="handleChangeCellDotLeader"
              >
                Dots
              </b-form-checkbox>
            </div>

            <div class="table-cell-panel__borders d-flex flex-wrap align-items-end">
              <div>
                <label class="builder-label">
                  Text
                </label>
                <div class="table-cell-panel__swatch">
                  <ColorPicker
                    v-model="selectedCellTextColor"
                    @input="(value) => handleChangeCellStyleRule('color', value, '')"
                  />
                </div>
              </div>

              <div>
                <label class="builder-label">
                  Cell
                </label>
                <div class="table-cell-panel__swatch">
                  <ColorPicker
                    v-model="selectedCellBgColor"
                    :show-color-table="true"
                    :table-color-value="selectedCellBgTableColorValue"
                    @color-table-change="handleCellBgColorTableChange"
                    @input="(value) => handleChangeCellStyleRule('background-color', value, '')"
                  />
                </div>
              </div>

              <div>
                <label class="builder-label">
                  Border
                </label>
                <div class="table-cell-panel__swatch">
                  <ColorPicker
                    v-model="selectedCellBorderColor"
                    :show-color-table="true"
                    :table-color-value="selectedCellTcBorderColor"
                    @color-table-change="changeCellTcBordersColors"
                    @input="(value) => changeCellBorders(BORDER_STYLE_RULES.BORDER_COLOR, value, '')"
                  />
                </div>
              </div>

              <div class="table-cell-panel__border-width">
                <label
                  class="builder-label"
                  for="cell-border-width"
                >
                  Border width
                </label>
                <b-input
                  id="cell-border-width"
                  v-model="selectedCellBorderWidth"
                  type="number"
                  min="1"
                  size="sm"
                  @input="(value) => changeCellBorders('border-width', value, 'px')"
                />
              </div>

              <div class="table-cell-panel__border-style">
                <label
                  class="builder-label"
                  for="border-style"
                >
                  Style
                </label>
                <select
                  id="border-style"
                  class="custom-select custom-select-sm"
                  :value="borderStyle"
                  @input="setBorderStyle"
                >
                  <option
                    v-for="(item, index) in borderStyleOptions"
                    :key="item + index"
                    :value="item"
                  >
                    {{ item }}
                  </option>
                </select>
              </div>

              <b-form-radio-group
                v-model="applyToBorders"
                class="builder-segmented"
                :options="applyToBordersOptions"
                button-variant="outline-secondary"
                size="sm"
                buttons
                @change="changeBorderOption"
              />
            </div>

            <div class="table-cell-panel__apply d-flex flex-wrap align-items-center">
              <label class="builder-label mb-0">
                Apply to
              </label>
              <b-form-radio-group
                v-model="applyToCells"
                class="builder-segmented"
                :options="applyToOptions"
                button-variant="outline-secondary"
                size="sm"
                buttons
              />

              <button
                type="button"
                class="builder-btn builder-btn--icon"
                title="Put an image into the selected cells"
                @click="showFileUploadModal"
              >
                <LucideIcon
                  name="image"
                  :size="16"
                />
              </button>

              <button
                type="button"
                class="builder-btn builder-btn--icon"
                :title="columnStyles.length ? 'Click a cell to paste the copied styles onto its column' : 'Copy the styles of this column'"
                @click="copyColumnStyle"
              >
                <LucideIcon
                  :name="columnStyles.length ? 'clipboard-check' : 'clipboard'"
                  :size="16"
                />
              </button>
            </div>
          </div>

          <div class="table-cell-panel__span">
            <div class="d-flex">
              <div class="mr-2">
                <label
                  class="builder-label"
                  for="cell-colspan"
                >
                  Colspan
                </label>
                <b-input
                  id="cell-colspan"
                  :value="selectedCells[0].colspan"
                  type="number"
                  class="table-cell-panel__span-input"
                  :disabled="selectedCells.length > 1"
                  size="sm"
                  @input="(value) => selectedCells[0].colspan = value"
                />
              </div>
              <div>
                <label
                  class="builder-label"
                  for="cell-rowspan"
                >
                  Rowspan
                </label>
                <b-input
                  id="cell-rowspan"
                  :value="selectedCells[0].rowspan"
                  type="number"
                  class="table-cell-panel__span-input"
                  :disabled="selectedCells.length > 1 || isCellHead"
                  size="sm"
                  @input="(value) => selectedCells[0].rowspan = value"
                />
              </div>
            </div>

            <div class="d-flex mt-2">
              <button
                type="button"
                class="builder-btn flex-fill justify-content-center mr-2"
                @click="onCancel"
              >
                Cancel
              </button>
              <button
                type="button"
                class="builder-btn builder-btn--primary flex-fill justify-content-center"
                @click="onApply"
              >
                Apply
              </button>
            </div>

            <div
              class="table-cell-panel__hint"
              :title="columnWidthsHint.title"
            >
              Sum column widths: {{ setColumnWidthsSum }}
              <span v-if="renderedColumnsWidth && renderedColumnsWidth !== setColumnWidthsSum">
                → renders {{ renderedColumnsWidth }}
              </span>
              <span v-if="columnsToTableGap">
                + {{ columnsToTableGap }} {{ columnWidthsHint.gapLabel }}
              </span>
              <span v-if="tableOuterWidth">
                = {{ tableOuterWidth }} (W)
              </span>
            </div>
            <div
              v-if="columnOverflows.length"
              class="table-cell-panel__hint table-cell-panel__hint--danger"
            >
              Some columns need more room than they were given:
              <span
                v-for="(overflow, index) in columnOverflows"
                :key="overflow.requested + '_' + overflow.used"
                class="font-bold"
              >{{ index ? ', ' : '' }}{{ overflow.requested }} → {{ overflow.used }}</span>
            </div>
            <div
              v-if="columnsSurplus > 1"
              class="table-cell-panel__hint"
            >
              The table is <span class="font-bold">{{ columnsSurplus }}px</span> wider than its
              columns need, so the extra is shared out across all of them. Set W to
              <span class="font-bold">{{ naturalTableWidth }}</span> to stop it.
            </div>
            <div
              v-if="columnsSqueeze > 1"
              class="table-cell-panel__hint table-cell-panel__hint--danger"
            >
              The table is <span class="font-bold">{{ columnsSqueeze }}px</span> narrower than
              its columns need, so every column is squeezed below the width it was given. Set W
              to <span class="font-bold">{{ naturalTableWidth }}</span> to give them the full
              amount.
            </div>
          </div>
        </div>

        <div class="builder-label table-preview-label">
          Preview
        </div>
      </div>

      <div
        v-if="table && table.rows.length"
        ref="tableContainer"
        class="table-builder-container"
        :class="{ 'table-builder-container--fullscreen': isFullscreen }"
      >
        <div
          class="table-zoom-stage"
          :style="zoomStageStyle"
        >
          <div
            class="table-zoom-source"
            :style="zoomSourceStyle"
          >
            <table
              ref="table"
              class="no-select"
              :style="getStyleOfTable"
            >
              <tbody>
                <!-- One list so the preview, the exported HTML and row removal can never disagree
                     about the order: static rows sit in their band or under the row they are anchored to. -->
                <tr
                  v-for="visualRow in visualRows"
                  :key="visualRow.key"
                  :style="visualRow.style"
                >
                  <td
                    v-for="(cell, index) in visualRow.cells"
                    :key="cell.id + '_' + index"
                    :style="cell.stringStyles"
                    :colspan="cell.colspan"
                    :rowspan="cell.rowspan"
                    :class="{ 'selected-cell': selectedCells.some(item => item.id === cell.id), 'editing-cell': editingCellId === cell.id }"
                    @mousedown="(event) => onCellMouseDown(event, cell)"
                  >
                    <img
                      v-if="cell.src"
                      :src="cell.src"
                      style="width: 100%; height: 100%;"
                      alt="img"
                    >
                    <div
                      v-else
                      class="cell-content-shell"
                    >
                      <div
                        class="cell-editor"
                        :class="{ 'cell-editor-inactive': editingCellId !== cell.id }"
                        v-bind="editingCellId === cell.id ? { contenteditable: 'true' } : {}"
                        @input="(event) => onCellTextInput(event, cell)"
                        @blur="(event) => onCellTextBlur(event, cell)"
                        @keydown="(event) => onCellKeyDown(event, cell)"
                        @focus="(event) => onCellFocus(event, cell)"
                        v-html="cell.valueHtml || ''"
                      />
                      <div
                        v-if="editingCellId !== cell.id"
                        class="cell-display"
                        v-html="getRenderedCellHtml(cell)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div
        v-else
        class="d-flex justify-content-center"
      >
        <b-dropdown
          text="Create Table"
          menu-class="table-create-menu"
        >
          <div class="p-2">
            <div class="d-flex">
              <b-form-group class="w-50 pr-1">
                <label>Columns</label>
                <b-form-input
                  v-model.number="createTable.columns"
                  type="number"
                  min="1"
                  step="1"
                  size="sm"
                  :state="isValidGridSize(createTable.columns) ? null : false"
                />
              </b-form-group>
              <b-form-group class="w-50 pl-1">
                <label>Rows</label>
                <b-form-input
                  v-model.number="createTable.rows"
                  type="number"
                  min="1"
                  step="1"
                  size="sm"
                  :state="isValidGridSize(createTable.rows) ? null : false"
                />
              </b-form-group>
            </div>

            <b-button
              variant="primary"
              size="sm"
              class="w-100"
              :disabled="!canCreateTable"
              @click="onCreateTable"
            >
              Create
            </b-button>
          </div>
        </b-dropdown>
      </div>
      <template #modal-footer="{ ok, cancel }">
        <div class="d-flex">
          <button
            type="button"
            class="builder-btn builder-btn--lg mr-2"
            @click="cancel()"
          >
            Close
          </button>
          <button
            type="button"
            class="builder-btn builder-btn--lg builder-btn--primary"
            @click="ok()"
          >
            Save table
          </button>
        </div>
      </template>
    </b-modal>

    <TableFileUploadModal
      ref="tableFileUploadModal"
      :src="selectedCellUrl"
      @save="handleAddImage"
    />
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import {EVENTS, simpleTableBuilderEventBroker} from '@frontend/group/modules/simple-table-builder/event-broker';
import DropdownCounter from '../../../common/atoms/DropdownCounter.vue';
import LucideIcon from '../../../common/atoms/LucideIcon.vue';
import InfoHint from '../../../common/atoms/InfoHint.vue';
import ColorPicker from '../EditorControl/components/ColorPicker.vue';
import FontFamilyManager from '../../../../common/load-fonts';
import {
  APPLY_TO_BORDERS_HANDLERS,
  APPLY_TO_BORDERS_LABEL,
  APPLY_TO_BORDERS_VALUES,
  APPLY_TO_TEXT,
  APPLY_TO_VALUES,
  BORDER_STYLE_OPTIONS,
  BORDER_STYLE_RULES,
  CELL_ALIGNMENTS,
  CELL_ANGLE_STYLE_RULE,
  DEFAULT_CELL_HORIZONTAL_ALIGN,
  DEFAULT_CELL_VERTICAL_ALIGN,
  TC_BORDER_COLOR_KEYS
} from "@frontend/group/modules/simple-table-builder/constants";
import {
  applyCellAngleFits,
  fitTableCellsWithAngle,
  measureCellAngleFit,
  wrapCellAngleHtml
} from "@frontend/group/modules/simple-table-builder/helpers";
import TableFileUploadModal from "@frontend/components/modules/layouts-page/modals/TableFileUploadModal.vue";
import {mapGetters} from "vuex";
import {ThemesContainerSingleton} from "@/banner_template/themes/ThemesContainerSinglton";
import Multiselect from "vue-multiselect";
import DOMPurify from "dompurify";
/** @var {Table} tableInstance */
let tableInstance;
let timeout;
let tableResizeObserver;

const ADDED_NOTICE_MS = 1500;

export default {
  name: 'SimpleTableBuilderModal',
  components: {
    Multiselect,
    TableFileUploadModal,
    DropdownCounter,
    ColorPicker,
    LucideIcon,
    InfoHint
  },
  data() {
    return {
      $element: null,
      table: null,
      params: null,
      tableData: null,
      createTable: {
        columns: 10,
        rows: 10
      },
      selectedCells: [],
      selectedCellBgColor: '#ffffff',
      selectedCellTextColor: '#000000',
      selectedCellTextSize: 25,
      selectedCellWidth: '1',
      selectedCellMinWidth: '1',
      selectedCellMinHeight: '1',
      selectedCellBorderColor: '#000000',
      selectedCellTcBorderColor: TC_BORDER_COLOR_KEYS.DISABLED,
      selectedCellBgTableColorValue: TC_BORDER_COLOR_KEYS.DISABLED,
      tableWidth: 0,
      tableHeight: 0,
      userChangedTableWidth: false,
      fontFamilyManager: FontFamilyManager.getInstance(),
      fontsList: [],
      fontFamily: 'Nunito-Bold-257',
      align: `${DEFAULT_CELL_HORIZONTAL_ALIGN},${DEFAULT_CELL_VERTICAL_ALIGN}`,
      textAlignedList: CELL_ALIGNMENTS,
      applyToCells: APPLY_TO_VALUES.SELECTED,
      autoHeight: true,
      autoFontSize: false,
      tableColumnMode: false,
      mergeSameValues: false,
      truncateLongText: false,
      borderBoxWidths: false,
      applyToBorders: APPLY_TO_BORDERS_VALUES.ALL,
      applyToBordersOptions: [
        { text: APPLY_TO_BORDERS_LABEL.ALL, value: APPLY_TO_BORDERS_VALUES.ALL },
        { text: APPLY_TO_BORDERS_LABEL.OUTER, value: APPLY_TO_BORDERS_VALUES.OUTER },
        { text: APPLY_TO_BORDERS_LABEL.INNER, value: APPLY_TO_BORDERS_VALUES.INNER },
        { text: APPLY_TO_BORDERS_LABEL.LEFT, value: APPLY_TO_BORDERS_VALUES.LEFT },
        { text: APPLY_TO_BORDERS_LABEL.RIGHT, value: APPLY_TO_BORDERS_VALUES.RIGHT },
        { text: APPLY_TO_BORDERS_LABEL.TOP, value: APPLY_TO_BORDERS_VALUES.TOP },
        { text: APPLY_TO_BORDERS_LABEL.BOTTOM, value: APPLY_TO_BORDERS_VALUES.BOTTOM },
      ],
      borderStyle: 'solid',
      borderStyleOptions: BORDER_STYLE_OPTIONS,
      selectedCellBorderWidth: 1,
      setColumnWidthsSum: 0,
      renderedColumnsWidth: 0,
      naturalColumnsWidth: 0,
      columnsSurplus: 0,
      tableOuterWidth: 0,
      naturalTableWidth: 0,
      columnOverflows: [],
      selectedCellUrl: '',
      selectedCellDotLeaderEnabled: false,
      columnStyles: [],
      isAdditionalTableField: false,
      selectedStyle: null,
      headersVisible: true,
      isOldTransposedTable: false,
      editingCellId: null,
      originalCellValue: '',
      activeTagContext: [],
      showFormatMenu: false,
      selectedPaddings: "2 5",
      selectedCellAngle: 0,
      isFullscreen: false,
      zoomLevel: 75,
      zoomNaturalWidth: null,
      zoomNaturalHeight: null,
      pendingScrollAxis: null,
    };
  },
  computed: {
    BORDER_STYLE_RULES() {
      return BORDER_STYLE_RULES
    },
    hasCellAngle() {
      return !!parseFloat(this.selectedCellAngle)
    },
    // What stands between the columns' total and the table's own width: the cells' paddings and
    // borders, or with border-box widths only the table's own border.
    columnsToTableGap() {
      return Math.max(0, this.tableOuterWidth - this.renderedColumnsWidth);
    },
    // A table pinned narrower than its columns need squeezes every one of them below the width it
    // was given — the mirror image of the surplus, and just as invisible without saying so.
    columnsSqueeze() {
      return Math.max(0, this.naturalColumnsWidth - this.renderedColumnsWidth);
    },
    // Names what stands between the columns' total and W, so the line always adds up to the number
    // in the W field whichever box model the table uses.
    columnWidthsHint() {
      if (this.borderBoxWidths) {
        return {
          gapLabel: 'table border',
          title: "A column width is its whole box — the cell's paddings and borders are inside it. Only the table's own border sits outside the columns.",
        };
      }

      return {
        gapLabel: 'paddings & borders',
        title: "A column width is its text area — the cells' paddings and borders are added on top of it, along with the table's own border.",
      };
    },
    ...mapGetters('layout',['selectedInstance']),
    themeStyles() {
      const theme = ThemesContainerSingleton.getByIdSync(this.selectedInstance?.data?.theme);
      let styles = theme?.attributes?.find(attr => _.get(attr, 'name') === 'Styles')?.list || [];
      if (!styles?.length && window.selectedTheme) {
        styles = window.selectedTheme?.attributes?.find(attr => _.get(attr, 'name') === 'Styles')?.list || [];
      }
      return styles;
    },
    getStyleOfTable() {
      if (!this.table) return {};

      const rules = this.table.styles.rules;

      return {
        ...rules,
      };
    },

    applyToOptions() {
      const disabled = this.table.headers.some(cell => cell.id === this.selectedCells[0].id);

      return [
        { text: APPLY_TO_TEXT.SELECTED, value: APPLY_TO_VALUES.SELECTED },
        { text: APPLY_TO_TEXT.ALL, value: APPLY_TO_VALUES.ALL },
        { text: APPLY_TO_TEXT.HEADER, value: APPLY_TO_VALUES.HEADER },
        { text: APPLY_TO_TEXT.BODY, value: APPLY_TO_VALUES.BODY },
        { text: APPLY_TO_TEXT.COLUMN, value: APPLY_TO_VALUES.COLUMN, disabled },
        { text: APPLY_TO_TEXT.ALTERNATING_ROWS, value: APPLY_TO_VALUES.ALTERNATING_ROWS, disabled }
      ]
    },
    isCellHead() {
      if (this.selectedCells.length > 1) return true;
      if (this.selectedCells.length < 1) return false;

      return tableInstance && Array.isArray(tableInstance.headers) && tableInstance.headers.findIndex(item => item.id === this.selectedCells[0].id) >= 0;
    },
    transposedRows() {
      if (!this.table || !this.table.rows.length) return [];

      return tableInstance.toData().rows;
    },

    topStaticRows() {
      if (!this.table) return [];

      if (Array.isArray(this.table.staticRows)) {
        return this.table.staticRows.filter(row => (row.position || 'top') === 'top');
      }

      return this.table.staticHeaders || [];
    },

    bottomStaticRows() {
      if (!this.table) return [];

      if (Array.isArray(this.table.staticRows)) {
        return this.table.staticRows.filter(row => (row.position || 'top') === 'bottom');
      }

      return this.table.staticFooters || [];
    },

    topStaticRowGroups() {
      return this.groupStaticRows(this.topStaticRows);
    },

    bottomStaticRowGroups() {
      return this.groupStaticRows(this.bottomStaticRows);
    },

    // The label that identifies the selected cell: the cell above it, or the one to its left when
    // there is nothing above. That is where a spec sheet keeps the name of the value.
    labelForSelectedCell() {
      const selectedCell = this.selectedCells[0];

      if (!selectedCell || !this.visualRows.length) return '';

      // Grid columns, not cell indexes: a merged cell above shifts every index after it.
      const grid = this.visualGrid;
      const rowIndex = grid.findIndex(row => row.some(cell => cell && cell.id === selectedCell.id));

      if (rowIndex < 0) return '';

      const column = grid[rowIndex].findIndex(cell => cell && cell.id === selectedCell.id);
      const text = cell => (cell && cell.id !== selectedCell.id ? String(cell.value || '').trim() : '');
      // A cell that spans several rows is a value running alongside them, never their label.
      const above = grid[rowIndex - 1]?.[column];
      const labelAbove = above && Number(above.rowspan || 1) === 1 ? text(above) : '';

      return labelAbove || text(grid[rowIndex][column - 1]);
    },

    // visualRows laid on the column grid: grid[row][column] is the cell covering that slot,
    // colspan and rowspan included (the same occupancy walk as Table.measureColumns).
    visualGrid() {
      const grid = [];
      const pending = new Map();

      this.visualRows.forEach((row, rowIndex) => {
        const slots = [];
        let column = 0;

        row.cells.forEach(cell => {
          while (pending.has(`${rowIndex}:${column}`)) {
            slots[column] = pending.get(`${rowIndex}:${column}`);
            column += 1;
          }

          const colspan = Math.max(1, Number(cell.colspan) || 1);
          const rowspan = Math.max(1, Number(cell.rowspan) || 1);

          for (let c = 0; c < colspan; c += 1) {
            slots[column + c] = cell;

            for (let r = 1; r < rowspan; r += 1) {
              pending.set(`${rowIndex + r}:${column + c}`, cell);
            }
          }

          column += colspan;
        });

        while (pending.has(`${rowIndex}:${column}`)) {
          slots[column] = pending.get(`${rowIndex}:${column}`);
          column += 1;
        }

        grid.push(slots);
      });

      return grid;
    },

    // A static cell can only be split while it still covers more than one column — the new cell
    // takes the last of them.
    // Mirrors Table.getVisualRows: the static rows sit in their band, or straight after the body
    // row they are anchored to.
    visualRows() {
      if (!this.table) return [];

      const bodyRows = this.tableColumnMode ? this.transposedRows : this.table.rows;
      const headerStyle = this.table.headerRowStyles.getStyle();
      const bands = { top: [], bottom: [] };
      const anchored = new Map();

      [...this.topStaticRowGroups, ...this.bottomStaticRowGroups].forEach(group => {
        const anchor = group[0].afterRowIndex;

        if (!Number.isFinite(anchor)) {
          bands[(group[0].position || 'top') === 'bottom' ? 'bottom' : 'top'].push(group);
          return;
        }

        const key = Math.min(Math.max(anchor, -1), bodyRows.length - 1);

        anchored.set(key, [...(anchored.get(key) || []), group]);
      });

      const asStatic = group => ({ kind: 'static', key: `static_${group[0].id}`, cells: group, style: headerStyle });
      const rows = bands.top.map(asStatic);

      if (this.table.headers && this.table.headers.length && this.headersVisible) {
        rows.push({ kind: 'header', key: 'header', cells: this.table.headers, style: headerStyle });
      }

      (anchored.get(-1) || []).forEach(group => rows.push(asStatic(group)));

      bodyRows.forEach((row, index) => {
        rows.push({ kind: 'body', key: `${row.id}_${index}`, cells: row.cells, style: row.stringStyles });
        (anchored.get(index) || []).forEach(group => rows.push(asStatic(group)));
      });

      return [...rows, ...bands.bottom.map(asStatic)];
    },

    // A static row can be anchored under the selected body row; header and static cells have no
    // body row of their own.
    canAddStaticRowAtSelection() {
      return !!this.selectedCells.length
        && (this.selectedIsStaticCell || this.getRowIndex(this.selectedCells[0].id) >= 0);
    },

    selectedIsStaticCell() {
      const selectedCell = this.selectedCells[0];

      return !!selectedCell
        && [...this.topStaticRows, ...this.bottomStaticRows].some(cell => cell.id === selectedCell.id);
    },

    canAddStaticCol() {
      const selectedCell = this.selectedCells[0];

      if (!selectedCell) return false;

      return [...this.topStaticRows, ...this.bottomStaticRows]
        .some(cell => cell.id === selectedCell.id && (cell.colspan || 1) > 1);
    },

    canCreateTable() {
      return this.isValidGridSize(this.createTable.columns)
        && this.isValidGridSize(this.createTable.rows);
    },

    transposedHeaders() {
      return this.topStaticRows?.length
        ? this.topStaticRows
        : [{id: 'static-header', value: ''}, ...this.table.rows.map(r => ({id: r.id, value: r.label || ''}))];
    },

    hideHeadersToggle: {
      get() {
        return !this.headersVisible;
      },
      set(value) {
        this.headersVisible = !value;
      }
    },

    canInsertRowAbove() {
      return this.selectedCellPosition.row >= 0;
    },

    canInsertRowBelow() {
      return this.selectedCellPosition.row >= 0;
    },

    canInsertColumnLeft() {
      return this.selectedCellPosition.column >= this.firstBodyColumn;
    },

    canInsertColumnRight() {
      return this.selectedCellPosition.column >= this.firstBodyColumn;
    },

    canMoveRowUp() {
      return this.selectedCellPosition.row > 0;
    },

    canMoveRowDown() {
      const { row } = this.selectedCellPosition;
      return row >= 0 && row < this.table.rows.length - 1;
    },

    // In Column Mode the first on-screen column is the headers; body rows can't go left of it.
    firstBodyColumn() {
      return this.table && this.table.tableColumnMode && this.table.headerVisible !== false ? 1 : 0;
    },

    canMoveColumnLeft() {
      return this.selectedCellPosition.column > this.firstBodyColumn;
    },

    canMoveColumnRight() {
      const { column, lastColumn } = this.selectedCellPosition;
      return column >= this.firstBodyColumn && column < lastColumn;
    },

    selectedCellPosition() {
      const none = { row: -1, column: -1, lastColumn: -1 };
      if (!this.selectedCells.length || !this.table) return none;

      const cellId = this.selectedCells[0].id;
      const lines = [this.table.headers, ...this.table.rows.map(row => row.cells)];
      const lineIndex = lines.findIndex(cells => cells.some(cell => cell.id === cellId));
      if (lineIndex < 0) return none;

      const cells = lines[lineIndex];
      return {
        row: lineIndex - 1,
        column: cells.findIndex(cell => cell.id === cellId),
        lastColumn: cells.length - 1,
      };
    },

    canRemoveRow() {
      return !!this.table && tableInstance.canRemoveRows(this.selectedCells);
    },

    canRemoveColumn() {
      return !!this.table && tableInstance.canRemoveColumns(this.selectedCells);
    },

    // Never explicitly sized here — that would let the stage's smaller width squeeze the table.
    zoomSourceStyle() {
      return {
        transform: `scale(${this.zoomLevel / 100})`,
        transformOrigin: 'top left',
      };
    },
    // Unconstrained ({}) until the observer reports a real size, so a 0-sized stage never squeezes the table.
    zoomStageStyle() {
      if (this.zoomNaturalWidth === null || this.zoomNaturalHeight === null) {
        return {};
      }

      const factor = this.zoomLevel / 100;

      return {
        width: `${Math.ceil(this.zoomNaturalWidth * factor)}px`,
        height: `${Math.ceil(this.zoomNaturalHeight * factor)}px`,
      };
    }
  },
  watch: {
    zoomNaturalWidth() {
      this.consumePendingScroll();
    },
    zoomNaturalHeight() {
      this.consumePendingScroll();
    }
  },
  mounted() {
    simpleTableBuilderEventBroker.on(EVENTS.INIT, this.init.bind(this));
    window.addEventListener('keydown', this.onKeyDown);
    this.fontFamilyManager.loadData();
    window.fontFamilyManager = this.fontFamilyManager;
  },
  beforeDestroy() {
    this.detachTableResizeObserver();
    window.removeEventListener('keydown', this.onKeyDown);
  },
  methods: {
    getRenderedCellHtml(cell) {
      if (!cell) return '';

      const rules = cell.styles?.rules || {};

      return wrapCellAngleHtml(this.getCellContentHtml(cell), rules[CELL_ANGLE_STYLE_RULE], rules);
    },
    getCellContentHtml(cell) {
      if (this.editingCellId === cell.id || !cell.dotLeaderEnabled || cell.src) {
        return cell.valueHtml || '';
      }

      const align = String(cell.styles?.rules?.['text-align'] || 'left').toLowerCase();
      if (!['left', 'right'].includes(align)) {
        return cell.valueHtml || '';
      }

      const gap = this.getCellLeaderGap(cell);
      const paddingLeft = this.getCellPaddingPx(cell, 'left');
      const paddingRight = this.getCellPaddingPx(cell, 'right');
      const leaderHtml = `<span aria-hidden="true" style="${this.getCellLeaderStyle(cell)}"></span>`;
      const wrapperStyle = 'display:flex; align-items:flex-end; width:100%; min-width:0;';
      const contentStyle = `flex:0 1 auto; min-width:0; white-space:pre-line; text-align:${align};`;
      const leaderWrapStyle = 'flex:1 1 auto; min-width:0;';

      if (align === 'right') {
        return [
          `<span style="${wrapperStyle} pointer-events:none;">`,
          `<span style="${leaderWrapStyle} pointer-events:none; margin-left:-${paddingLeft}px; padding-right:${gap}px;">${leaderHtml}</span>`,
          `<span data-cell-dot-leader="content" style="${contentStyle} pointer-events:none;">${cell.valueHtml}</span>`,
          '</span>',
        ].join('');
      }

      return [
        `<span style="${wrapperStyle} pointer-events:none;">`,
        `<span data-cell-dot-leader="content" style="${contentStyle} pointer-events:none; padding-right:${gap}px;">${cell.valueHtml}</span>`,
        `<span style="${leaderWrapStyle} pointer-events:none; margin-right:-${paddingRight}px;">${leaderHtml}</span>`,
        '</span>',
      ].join('');
    },
    getCellLeaderGap(cell) {
      const fontSize = parseFloat(cell?.styles?.rules?.['font-size']);
      const base = Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 16;
      return Math.max(1, Math.round(base * 0.08));
    },
    getCellPaddingPx(cell, side) {
      const value = parseFloat(cell?.styles?.rules?.[`padding-${side}`]);
      return Number.isFinite(value) && value > 0 ? value : 0;
    },
    getCellLeaderStyle(cell) {
      const fontSize = parseFloat(cell?.styles?.rules?.['font-size']);
      const base = Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 16;
      const dotSize = Math.max(1, Math.round(base * 0.09));
      const dotStep = Math.max(3, Math.round(base * 0.3));
      const offsetY = Math.max(3, Math.round(base * 0.38));

      return [
        'display:block',
        'width:100%',
        'min-width:0',
        'overflow:hidden',
        `height:${dotSize + 2}px`,
        'align-self:flex-end',
        `transform:translateY(-${offsetY}px)`,
        'background-repeat:space no-repeat',
        `background-size:${dotStep}px ${dotSize + 2}px`,
        `background-image:radial-gradient(circle, currentColor ${dotSize}px, transparent ${dotSize + 0.2}px)`,
        'background-position:left center',
      ].join(';');
    },
    applyBoldTag() {
      const cell = this.selectedCells?.[0];
      if (!cell) return;

      const appliedInEditor = this.applyTagToEditorSelection(cell, 'b');

      if (appliedInEditor) return;

      this.wrapTextareaSelection(cell, 'b');

      this.$nextTick(() => {
        const editor = this.getActiveCellEditor(cell.id);
        if (editor) {
          editor.innerHTML = cell.valueHtml || '';
        }
      });
    },
    applySupTag() {
      const cell = this.selectedCells?.[0];
      if (!cell) return;

      const appliedInEditor = this.applyTagToEditorSelection(cell, 'sup');

      if (appliedInEditor) return;

      this.wrapTextareaSelection(cell, 'sup');

      this.$nextTick(() => {
        const editor = this.getActiveCellEditor(cell.id);
        if (editor) {
          editor.innerHTML = cell.valueHtml || '';
        }
      });
    },
    applySubTag() {
      const cell = this.selectedCells?.[0];
      if (!cell) return;

      const appliedInEditor = this.applyTagToEditorSelection(cell, 'sub');

      if (appliedInEditor) return;

      this.wrapTextareaSelection(cell, 'sub');

      this.$nextTick(() => {
        const editor = this.getActiveCellEditor(cell.id);
        if (editor) {
          editor.innerHTML = cell.valueHtml || '';
        }
      });
    },
    getActiveCellEditor(cellId) {
      const editor = document.activeElement?.classList?.contains('cell-editor')
          ? document.activeElement
          : null;

      return editor && this.editingCellId === cellId ? editor : null;
    },
    setActiveTagContextFromNode(node, editor = null) {
      const activeEditor = editor || (document.activeElement?.classList?.contains('cell-editor')
          ? document.activeElement
          : null);

      if (!activeEditor || !node) {
        this.activeTagContext = [];
        return;
      }

      let current = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      const tags = [];

      while (current && current !== activeEditor) {
        if (current.tagName) {
          tags.push(current.tagName.toLowerCase());
        }
        current = current.parentElement;
      }

      this.activeTagContext = tags;
    },
    applyTagToEditorSelection(cell, tagName, style = {}) {
      const editor = this.getActiveCellEditor(cell.id);
      if (!editor) return false;

      const selection = window.getSelection?.();
      const range = selection && selection.rangeCount ? selection.getRangeAt(0) : null;

      if (!range || !editor.contains(range.commonAncestorContainer)) {
        return false;
      }

      const findTaggedAncestor = (node) => {
        let current = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;

        while (current && current !== editor) {
          if (current.tagName?.toLowerCase() === tagName.toLowerCase()) {
            return current;
          }
          current = current.parentElement;
        }

        return null;
      };

      const taggedAncestor = findTaggedAncestor(range.commonAncestorContainer);

      if (taggedAncestor && (range.collapsed || taggedAncestor.contains(range.commonAncestorContainer))) {
        const parent = taggedAncestor.parentNode;
        while (taggedAncestor.firstChild) {
          parent.insertBefore(taggedAncestor.firstChild, taggedAncestor);
        }
        parent.removeChild(taggedAncestor);

        const newHtml = this.getEditorHtml(editor);
        editor.innerHTML = newHtml;
        cell.valueHtml = newHtml;
        this.setActiveTagContextFromNode(selection?.anchorNode, editor);

        const newRange = document.createRange();
        newRange.selectNodeContents(editor);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);

        return true;
      }

      if (range.collapsed) {
        return false;
      }

      const el = document.createElement(tagName);
      Object.entries(style).forEach(([key, value]) => {
        el.style[key] = value;
      });

      range.surroundContents(el);

      const newHtml = this.getEditorHtml(editor);
      editor.innerHTML = newHtml;
      cell.valueHtml = newHtml;
      this.setActiveTagContextFromNode(selection?.anchorNode, editor);

      const newRange = document.createRange();
      newRange.selectNodeContents(editor);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);

      return true;
    },
    isTagActive(tagName) {
      const editor = document.activeElement?.classList?.contains('cell-editor')
          ? document.activeElement
          : null;

      if (!editor) {
        return this.activeTagContext.includes(tagName.toLowerCase());
      }

      const selection = window.getSelection?.();
      const node = selection?.anchorNode;

      if (!node || !editor.contains(node)) {
        return this.activeTagContext.includes(tagName.toLowerCase());
      }

      let current = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;

      while (current && current !== editor) {
        if (current.tagName?.toLowerCase() === tagName.toLowerCase()) {
          return true;
        }
        current = current.parentElement;
      }

      return this.activeTagContext.includes(tagName.toLowerCase());
    },
    wrapTextareaSelection(cell, tagName, styleText = '') {
      const ta = this.$refs.cellValueTa?.$el || this.$refs.cellValueTa;
      const text = String(cell.valueHtml || '');
      const start = ta?.selectionStart ?? 0;
      const end = ta?.selectionEnd ?? 0;

      const openTag = styleText
          ? `<${tagName} style="${styleText}">`
          : `<${tagName}>`;
      const closeTag = `</${tagName}>`;

      if (start === end) {
        const fullTagRegex = new RegExp(`^<${tagName}(\\s[^>]*)?>([\\s\\S]*)<\\/${tagName}>$`, 'i');
        const match = text.match(fullTagRegex);

        if (match) {
          cell.valueHtml = match[2] || '';
          return;
        }

        cell.valueHtml = `${openTag}${text || 'Column'}${closeTag}`;
        return;
      }

      const before = text.slice(0, start);
      const selected = text.slice(start, end) || 'Column';
      const after = text.slice(end);

      const selectedTagRegex = new RegExp(`^<${tagName}(\\s[^>]*)?>([\\s\\S]*)<\\/${tagName}>$`, 'i');
      const selectedMatch = selected.match(selectedTagRegex);

      if (selectedMatch) {
        cell.valueHtml = `${before}${selectedMatch[2] || ''}${after}`;
        return;
      }

      cell.valueHtml = `${before}${openTag}${selected}${closeTag}${after}`;
    },

    replaceTextareaSelection(cell, replacement, fallback = replacement) {
      const ta = this.$refs.cellValueTa?.$el || this.$refs.cellValueTa;
      const text = String(cell.valueHtml || '');
      const start = ta?.selectionStart ?? 0;
      const end = ta?.selectionEnd ?? 0;

      if (start === end) {
        cell.valueHtml = text.slice(0, start) + fallback + text.slice(start);
        return;
      }

      const before = text.slice(0, start);
      const selected = text.slice(start, end);
      const after = text.slice(end);

      cell.valueHtml = before + replacement(selected) + after;
    },
    insertNbsp() {
      const cell = this.selectedCells?.[0];
      if (!cell) return;

      const editor = this.getActiveCellEditor(cell.id);
      if (editor) {
        const selection = window.getSelection?.();
        const range = selection && selection.rangeCount ? selection.getRangeAt(0) : null;

        if (range && editor.contains(range.commonAncestorContainer)) {
          const replacementText = range.collapsed
              ? '\u00A0'
              : range.toString().replace(/ /g, '\u00A0');

          range.deleteContents();
          range.insertNode(document.createTextNode(replacementText));

          const newHtml = this.getEditorHtml(editor);
          editor.innerHTML = newHtml;
          cell.valueHtml = newHtml;
          this.setActiveTagContextFromNode(selection?.anchorNode, editor);

          const newRange = document.createRange();
          newRange.selectNodeContents(editor);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
          return;
        }
      }

      this.replaceTextareaSelection(
          cell,
          selected => selected.replace(/ /g, '&nbsp;'),
          '&nbsp;'
      );

      this.$nextTick(() => {
        const activeEditor = this.getActiveCellEditor(cell.id);
        if (activeEditor) {
          activeEditor.innerHTML = cell.valueHtml || '';
        }
      });
    },
    insertFont() {
      const cell = this.selectedCells?.[0];
      if (!cell) return;

      const selectEl = this.$el.querySelector('#font-family');
      const font = selectEl?.value || this.fontFamily || 'Montserrat-ExtraBold';

      const appliedInEditor = this.applyTagToEditorSelection(cell, 'span', {
        fontFamily: font,
      });

      if (appliedInEditor) return;

      this.wrapTextareaSelection(cell, 'span', `font-family: ${font}`);

      this.$nextTick(() => {
        const editor = this.getActiveCellEditor(cell.id);
        if (editor) {
          editor.innerHTML = cell.valueHtml || '';
        }
      });
    },
    changeCellPaddings(value) {
      tableInstance.changeTablePaddingFromShorthand(value)
      // A rotated cell is sized only by its fitted Height / Min Width (the text itself
      // is out of flow), and those include the paddings — so new paddings make the
      // stored fit wrong until it is recomputed.
      fitTableCellsWithAngle(tableInstance, { force: true });
      this.table = tableInstance.toData();
      this.handleTableDimension();
    },
    async init({ $element, table, styles, params, tableData, isAdditionalTableField = false }) {
      this.clearData();
      this.$element = $element;
      this.params = params;
      this.tableData = tableData;
      tableInstance = table;
      this.table = tableInstance.toData();
      this.selectedPaddings = this.table.defaultCellPaddingShorthand
      this.headersVisible = tableInstance.headerVisible;

      const fontList = this.fontFamilyManager.getFontList();

      const individualFonts = Object.entries(fontList)
        .filter(([, v]) => !Array.isArray(v))
        .map(([k, v]) => ({ index: k, name: v }));

      this.fontsList = individualFonts.length ? individualFonts : this.fontFamilyManager.getAllFontsData();

      this.autoHeight = tableInstance.styles.rules.height === 'auto';
      this.autoFontSize = tableInstance.autoFontSize;
      this.tableColumnMode = tableInstance.tableColumnMode;
      this.mergeSameValues = tableInstance.mergeSameValues;
      this.truncateLongText = tableInstance.truncateLongText;
      this.borderBoxWidths = tableInstance.borderBoxWidths;
      //fix for old tables
      if (!tableInstance.headers.length && this.tableColumnMode === false) {
        this.tableColumnMode = true;
      }
      this.setColumnWidthsSum = tableInstance.getSumOfColumnWidths();
      this.isAdditionalTableField = isAdditionalTableField;

      this.$bvModal.show('simple-table-builder');

      this.handleTableDimension();

      this.$nextTick(() => {
        this.attachTableResizeObserver();
      });
    },
    clearData() {
      this.$element = null;
      this.table = null;
      this.params = null;
      this.createTable = {
        columns: 10,
        rows: 10
      };
      this.selectedCells = [];
      this.selectedCellBgColor = '#ffffff';
      this.selectedCellTextColor = '#000000';
      this.selectedCellMinWidth = '1';
      this.selectedCellTextSize = 16;
      this.selectedCellDotLeaderEnabled = false;
      this.selectedCellAngle = 0;
      this.applyToCells = APPLY_TO_VALUES.SELECTED;
      this.editingCellId = null;
      this.originalCellValue = '';
      this.activeTagContext = [];
      this.userChangedTableWidth = false;
      this.zoomLevel = 75;
      this.zoomNaturalWidth = null;
      this.zoomNaturalHeight = null;
      this.pendingScrollAxis = null;
    },
    zoomOut() {
      this.zoomLevel = Math.max(25, this.zoomLevel - 25);
    },
    zoomIn() {
      this.zoomLevel = Math.min(100, this.zoomLevel + 25);
    },
    attachTableResizeObserver() {
      this.detachTableResizeObserver();

      if (!this.$refs.table) return;

      tableResizeObserver = new ResizeObserver(() => {
        const el = this.$refs.table;
        if (!el) return;

        this.zoomNaturalWidth = el.offsetWidth;
        this.zoomNaturalHeight = el.offsetHeight;
      });

      tableResizeObserver.observe(this.$refs.table, { box: 'border-box' });
    },
    detachTableResizeObserver() {
      if (tableResizeObserver) {
        tableResizeObserver.disconnect();
        tableResizeObserver = null;
      }
    },
    isValidGridSize(value) {
      return Number.isInteger(value) && value >= 1;
    },
    onCreateTable() {
      if (!this.canCreateTable) return;

      this.tableColumnMode = false;
      this.autoHeight = false;

      const columns = this.createTable.columns;
      const rows = this.createTable.rows;

      tableInstance.createTableGrid(columns, rows);

      this.table = tableInstance.toData();

      this.$nextTick(async () => {
        this.attachTableResizeObserver();

        const tableDimension = await tableInstance.getDimension();

        this.tableWidth = tableDimension.width
        this.tableHeight = tableDimension.height
      });
    },
    // Cells of a static row are the ones sharing its staticRowIndex; a cell saved before static
    // columns existed has none and is a row on its own.
    groupStaticRows(cells) {
      return (cells || []).reduce((groups, cell) => {
        const group = groups[groups.length - 1];
        const sameRow = group
          && Number.isFinite(cell.staticRowIndex)
          && group[0].staticRowIndex === cell.staticRowIndex;

        sameRow ? group.push(cell) : groups.push([cell]);

        return groups;
      }, []);
    },
    addStaticCol() {
      if (!this.canAddStaticCol) return;

      tableInstance.addStaticColumnCell(this.selectedCells[0].id);

      this.table = tableInstance.toData();
    },
    addStaticRowAbove() {
      // Above the whole table, bands included — appending put it under the bands, right on the body.
      tableInstance.addStaticHeaderCell(0)

      this.table = tableInstance.toData();
    },
    addStaticRowBelow() {
      tableInstance.addStaticFooterCell()

      this.table = tableInstance.toData();
    },
    // The name goes onto the cell as it is typed: focusing this field already ended the cell's
    // edit, so the blur commit cannot carry it, and by the time the field loses focus the
    // selection may already be another cell.
    onMappingNameInput(value) {
      const selected = this.selectedCells[0];
      const cell = selected && tableInstance.getCellById(selected.id);

      if (!cell) return;

      cell.setMappingName(value);
      selected.mappingName = cell.getMappingName();
    },

    refreshTableData() {
      this.table = tableInstance.toData();
    },

    useLabelAsMappingName() {
      if (!this.labelForSelectedCell) return;

      this.selectedCells[0].mappingName = this.labelForSelectedCell;
      this.onApply();
    },
    addStaticRowAboveSelected() {
      this.addStaticRowAtSelection(-1);
    },
    addStaticRowBelowSelected() {
      this.addStaticRowAtSelection(0);
    },
    // -1 renders it under the header row, so "above the first body row" needs no special case.
    addStaticRowAtSelection(offset) {
      if (!this.canAddStaticRowAtSelection) return;

      if (this.selectedIsStaticCell) {
        tableInstance.addStaticRowNextTo(this.selectedCells[0].id, offset);
      } else {
        tableInstance.addStaticRowAnchoredTo(this.getRowIndex(this.selectedCells[0].id) + offset);
      }

      this.table = tableInstance.toData();
    },
    addRow() {
      tableInstance.addNewRow();

      this.table = tableInstance.toData();
      this.notifyAdded('Row added');
      this.requestScrollToEnd(this.tableColumnMode ? 'horizontal' : 'vertical');
    },
    addColumn() {
      // With a static cell selected the column being added is a static one: the cell splits on
      // a column edge, the table's own columns stay as they are.
      if (this.selectedIsStaticCell) {
        if (!this.canAddStaticCol) {
          this.notify('warning', 'Select a static cell that covers more than one column');
          return;
        }

        this.addStaticCol();
        this.notifyAdded('Static column added');
        return;
      }

      tableInstance.addNewColumn();

      this.table = tableInstance.toData();
      this.notifyAdded('Column added');
      this.requestScrollToEnd(this.tableColumnMode ? 'vertical' : 'horizontal');
    },
    notifyAdded(text) {
      this.notify('success', text);
    },
    notify(type, text) {
      toastr[type](text, '', {
        target: '#simple-table-builder .modal-content',
        positionClass: 'toast-table-builder',
        timeOut: ADDED_NOTICE_MS,
        extendedTimeOut: 0,
        preventDuplicates: true,
      });
    },
    scrollToEnd(axis) {
      requestAnimationFrame(() => {
        const container = this.$refs.tableContainer;
        if (!container) return;

        if (axis === 'vertical') {
          container.scrollTop = container.scrollHeight;
        } else {
          container.scrollLeft = container.scrollWidth;
        }
      });
    },
    // scrollToEnd's rAF can run before the zoom stage's ResizeObserver-driven size catches up
    // with the just-added row/column, under-scrolling; re-run once that size actually updates.
    requestScrollToEnd(axis) {
      this.pendingScrollAxis = axis;
      this.scrollToEnd(axis);
    },
    consumePendingScroll() {
      if (!this.pendingScrollAxis) return;

      const axis = this.pendingScrollAxis;
      this.pendingScrollAxis = null;
      this.scrollToEnd(axis);
    },
    removeRow() {
      tableInstance.removeRows(this.selectedCells);
      this.table = tableInstance.toData();
      this.selectedCells = [];
    },
    removeColumn() {
      tableInstance.removeColumns(this.selectedCells);
      this.table = tableInstance.toData();
      this.selectedCells = [];
    },
    setFontFamily(fontFamily) {
      this.fontObject = this.getFontObjectFromList(fontFamily.target.value);
      this.fontFamily = this.fontObject.index;
      this.handleChangeCellStyleRule('font-family', this.fontFamily, '');
    },
    getFontObjectFromList(font) {
      return this.fontsList.find(o => o.index === font);
    },
    setFontAligned(align) {
      this.align = align.target.value;

      const [horizontal, vertical] = this.align.split(',');

      this.handleChangeCellStyleRules([
        { rule: 'text-align', value: horizontal || DEFAULT_CELL_HORIZONTAL_ALIGN, measure: '' },
        { rule: 'vertical-align', value: vertical || DEFAULT_CELL_VERTICAL_ALIGN, measure: '' },
      ]);
    },
    setBorderStyle({ target }) {
      this.borderStyle = target.value;
      this.changeCellBorders('border-style', this.borderStyle, '');
    },
    onCellMouseDown(event, { id, value, link, colspan, rowspan, valueHtml, dotLeaderEnabled, mappingName }) {
      if (event.button === 0 && event.shiftKey ) {
        event.preventDefault();
        const index = this.selectedCells.findIndex(cell => cell.id === id);

        index >= 0
          ? this.selectedCells.splice(index, 1)
          : this.selectedCells.push({ id, value, link, colspan, rowspan, valueHtml, dotLeaderEnabled, mappingName });

      } else {
        this.selectedCells = [{ id, value, link, colspan, rowspan, valueHtml, dotLeaderEnabled, mappingName }];
        if (!tableInstance.getCellSrc(id)) {
          this.editingCellId = id;
          this.originalCellValue = valueHtml || '';

          this.$nextTick(() => {
            const editor = event.currentTarget?.querySelector('.cell-editor');
            editor?.focus();

            requestAnimationFrame(() => {
              const selectionNode = window.getSelection?.()?.anchorNode;
              this.setActiveTagContextFromNode(selectionNode, editor);
            });
          });
        }
      }

      if (!this.selectedCells.length) return;

      if (this.columnStyles.length) {
        this.applyStylesToColumn();
      }

      this.selectedCellTextColor = this.getCellStyle('background-color', '#ffffff');
      this.selectedCellTextColor = this.getCellStyle('color', '#000000');
      this.selectedCellTextSize = this.getCellStyle('font-size', 25);
      this.align = [
        this.getCellStyle('text-align', DEFAULT_CELL_HORIZONTAL_ALIGN),
        this.getCellStyle('vertical-align', DEFAULT_CELL_VERTICAL_ALIGN),
      ].join(',');
      this.fontFamily = this.getCellStyle('font-family', 'Nunito-Bold-257');
      // Column-mode tables store Height on the cell (not the <tr>), regardless of
      // whether the table is currently rendered transposed (headers present) or
      // flattened to stored rows (headers empty after import). So read the cell
      // height whenever the table is in column mode. In normal mode it's a row
      // style, read through the routed getStyle.
      this.selectedCellMinHeight = this.tableColumnMode
        ? this.getColumnModeOnScreenRowHeight(this.selectedCells[0].id)
        : this.getCellStyle('height', 0);
      this.selectedCellBorderColor = this.getCellBorderStyle(BORDER_STYLE_RULES.BORDER_COLOR, '#000000');
      this.selectedCellBorderWidth = this.getCellBorderStyle('border-width', 1);
      this.borderStyle = this.getCellBorderStyle('border-style', 'solid');
      this.selectedCellTcBorderColor = this.getCellBorderStyle(BORDER_STYLE_RULES.TC_BORDER_COLOR, TC_BORDER_COLOR_KEYS.DISABLED);
      this.selectedCellUrl = tableInstance.getCellSrc(this.selectedCells[0].id);
      this.selectedCellWidth = tableInstance.getStyle(this.selectedCells[0].id, 'width', '') !== 'auto'
        ? tableInstance.getStyle(this.selectedCells[0].id, 'width', '')
        : '';
      this.selectedCellMinWidth = tableInstance.getStyle(this.selectedCells[0].id, 'min-width', '') !== 'auto'
        ? tableInstance.getStyle(this.selectedCells[0].id, 'min-width', '')
        : '';
      this.selectedCellBgTableColorValue = this.getCellStyle('cell-bg-table-color-value', TC_BORDER_COLOR_KEYS.DISABLED)
      this.selectedCellBgColor = this.getCellStyle('background-color', TC_BORDER_COLOR_KEYS.DISABLED)
      this.selectedCellDotLeaderEnabled = tableInstance.getCellDotLeaderEnabled(this.selectedCells[0].id, false);
      this.selectedCellAngle = this.getCellStyle(CELL_ANGLE_STYLE_RULE, 0);
      this.matchSelectedStyle();
    },
    handleChangeCellStyleRule(rule, value, measure = '') {
      this.handleChangeCellStyleRules([{ rule, value, measure }]);
    },
    handleChangeCellStyleRules(rules) {
      if (this.applyToCells === APPLY_TO_VALUES.SELECTED) {
        tableInstance.changeCellStyle({
          cells: this.selectedCells.map(cell => cell.id),
          rules,
        });
      } else {
        tableInstance.changeMultipleCellsStyle(rules, this.applyToCells, this.selectedCells[0]);
      }

      this.table = tableInstance.toData();

      this.handleTableDimension();
    },
    // Cells that make up the on-screen row of `cellId` while in Column Mode.
    // Used by both the Height read and write so they stay in sync regardless of
    // which cell of the row is selected. The on-screen row depends on render
    // orientation: transposed (headers present) vs flattened (headers empty
    // after import, where the on-screen row is the stored row).
    columnModeOnScreenRowCells(cellId) {
      return tableInstance.getTableColumnMode()
        ? tableInstance.getColumnModeVisualRowCells(cellId)
        : tableInstance.getColumnModeVisualColumnCells(cellId);
    },

    // Effective height of the on-screen row a cell belongs to (Column Mode).
    // The rendered row height is the max of its cells' heights, so report that
    // instead of a single (possibly 0) cell — this keeps the value identical no
    // matter which cell in the row is selected, matching the backend editor.
    getColumnModeOnScreenRowHeight(cellId) {
      let max = 0;
      this.columnModeOnScreenRowCells(cellId).forEach(cell => {
        const h = parseInt(cell.styles?.rules?.height);
        if (!Number.isNaN(h) && h > max) {
          max = h;
        }
      });
      return max;
    },

    handleChangeCellHeight(value) {
      // "Height" always changes the on-screen vertical size of the selected cell.
      // Column-mode tables store Height on the cells (not the <tr>). Which cells
      // make up the on-screen row depends on whether the table is rendered
      // transposed:
      //  - transposed (headers present): the on-screen row is a stored column,
      //    so target the transposed visual row (applyColumnModeRowStyle);
      //  - flattened (headers empty after import): the on-screen row IS the
      //    stored row, so target that row's cells (applyColumnModeColumnStyle).
      // In normal (non column) mode it stays a row-level style as before.
      if (this.tableColumnMode) {
        if (!this.selectedCells.length) return;

        const raw = value == null ? '' : String(value).trim();
        const height = raw.length ? `${raw}px` : 'auto';
        const transposed = tableInstance.getTableColumnMode();

        this.selectedCells.forEach(cell => {
          if (transposed) {
            tableInstance.applyColumnModeRowStyle(cell.id, { height });
          } else {
            tableInstance.applyColumnModeColumnStyle(cell.id, { height });
          }
        });

        this.table = tableInstance.toData();
        this.handleTableDimension();
      } else {
        this.changeRowStyles('height', value, 'px');
      }
    },
    handleChangeCellAngle(value) {
      this.handleChangeCellStyleRule(CELL_ANGLE_STYLE_RULE, value, '');
      this.fitCellsToAngle();
    },

    // Fits the same cells the Angle was applied to, so "Apply to: All" does not leave
    // every cell but the selected one without room for the rotated text.
    fitCellsToAngle() {
      if (!this.selectedCells.length) return;

      const cells = this.applyToCells === APPLY_TO_VALUES.SELECTED
        ? this.selectedCells.map(selected => tableInstance.getCellById(selected.id)).filter(Boolean)
        : tableInstance.getCellsForApplyTo(this.applyToCells, this.selectedCells[0]);

      const fits = cells.reduce((acc, cell) => {
        const size = measureCellAngleFit(cell, tableInstance.styles);

        return size ? [...acc, { id: cell.id, size }] : acc;
      }, []);

      if (!fits.length) return;

      applyCellAngleFits(tableInstance, fits);

      // Read the height back: a fit takes the row up to the tallest rotated cell in it,
      // which is not necessarily the cell that was fitted.
      this.selectedCellMinHeight = this.tableColumnMode
        ? this.getColumnModeOnScreenRowHeight(this.selectedCells[0].id)
        : this.getCellStyle('height', 0);
      this.selectedCellMinWidth = Math.max(...fits.map(fit => fit.size.width));
      this.table = tableInstance.toData();
      this.setColumnWidthsSum = tableInstance.getSumOfColumnWidths();

      this.handleTableDimension();
    },

    changeRowStyles(rule, value, measure = '') {
      tableInstance.changeRowStyles({
        cells: this.selectedCells.map(cell => cell.id),
        rules: [{
          rule: rule,
          value: value,
          measure: measure
        }],
      });

      this.table = tableInstance.toData();

      this.handleTableDimension();
    },
    changeCellTcBordersColors(value) {
      this.changeCellBorders(BORDER_STYLE_RULES.TC_BORDER_COLOR, value, '');
    },
    changeCellBorders(rule, value, measure = '') {
      tableInstance.changeCellBorderStyle({
        cells: this.selectedCells.map(cell => cell.id),
        applyTo: this.applyToCells,
        handler: APPLY_TO_BORDERS_HANDLERS[this.applyToBorders],
        styleRule: {
          rule: rule,
          value: value,
          measure: measure,
          applyToBorder: this.applyToBorders
        },
      });

      this.table = tableInstance.toData();

      this.handleTableDimension();
    },
    changeTableWidth(width) {
      this.tableWidth = width;
      this.userChangedTableWidth = true;
      // Keep the height rule 'auto' while the toggle is on — writing the numeric
      // display height here would silently turn the table into fixed-height.
      tableInstance.setDimensions(this.widthToPersist(), this.autoHeight ? 'auto' : this.tableHeight);
      this.table = tableInstance.toData();

      this.scheduleColumnsReadout();
    },
    // The W input shows the MEASURED width (content can push past the stored rule — e.g. an
    // unmapped template table with all its columns renders far wider than its width rule).
    // Persisting that readout when the user never typed a width silently corrupts the stored
    // width, so untouched tables keep the width rule they came with.
    widthToPersist() {
      if (this.userChangedTableWidth) {
        return this.tableWidth;
      }

      const storedWidth = parseFloat(tableInstance.styles?.rules?.width);

      return Number.isFinite(storedWidth) ? storedWidth : this.tableWidth;
    },
    // Re-measure so the column readout follows the new width, but leave W alone — it is being
    // typed — and only touch H while auto-height owns it. Measuring builds an iframe and waits on
    // fonts, so it must not run on every keystroke.
    scheduleColumnsReadout() {
      if (!this.columnsReadout) {
        this.columnsReadout = _.debounce(() => {
          this.handleTableDimension({ syncWidth: false, syncHeight: this.autoHeight });
        }, 250);
      }

      this.columnsReadout();
    },
    changeTableHeight(height) {
      this.tableHeight = height;
      tableInstance.setDimensions(this.widthToPersist(), this.autoHeight ? 'auto' : this.tableHeight);
      this.table = tableInstance.toData();
    },
    handleChangeCellDotLeader(value) {
      if (!this.selectedCells.length) return;

      if (this.applyToCells === APPLY_TO_VALUES.SELECTED) {
        this.selectedCells.forEach((cell) => {
          tableInstance.changeMultipleCellsDotLeader(!!value, APPLY_TO_VALUES.SELECTED, cell);
        });
      } else {
        tableInstance.changeMultipleCellsDotLeader(!!value, this.applyToCells, this.selectedCells[0]);
      }

      this.selectedCells = this.selectedCells.map(cell => ({
        ...cell,
        dotLeaderEnabled: !!value,
      }));
      this.table = tableInstance.toData();
      this.handleTableDimension();
    },
    onApply() {
      tableInstance.changeCellValue(this.selectedCells[0]);
      this.table = tableInstance.toData();
    },
    onKeyDown(e) {
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        this.onApply();
      }
    },
    onCancel() {
      this.selectedCells = [];
      this.selectedCellDotLeaderEnabled = false;
    },
    getStyleValue(key, defaultValue) {
      const value = this.table.styles.rules[key];
      const number = parseInt(value);

      return value === undefined ? defaultValue : Number.isNaN(number) ? value : number;
    },
    onChangeStyleOfTable(key, value, measure = '', hasChangeForCells = false) {
      tableInstance.styles.setRules({
        [key]: `${value}${measure}`,
      });

      if (hasChangeForCells) {
        tableInstance.setCellsRules({
          [key]: `${value}${measure}`,
        });
      }

      this.table = tableInstance.toData();

      this.handleTableDimension();
    },
    onSave() {
      const height = this.autoHeight ? 'auto' : this.tableHeight;
      tableInstance.setDimensions(this.widthToPersist(), height);
      tableInstance.setAutoFontSize(this.autoFontSize);
      tableInstance.setTableColumnMode(this.tableColumnMode);
      tableInstance.setHeaderVisibility(this.headersVisible);
      tableInstance.setMergeSameValues(this.mergeSameValues);
      tableInstance.setTruncateLongText(this.truncateLongText);
      tableInstance.setBorderBoxWidths(this.borderBoxWidths);

      if (this.isAdditionalTableField) {
        simpleTableBuilderEventBroker.fire(EVENTS.ADD_ADDITIONAL_TABLE_FIELD, { tableInstance });

        this.isAdditionalTableField = false;

        return;
      }

      if (this.autoHeight) {
        tableInstance.setAutoFontSizeValue(null);

        simpleTableBuilderEventBroker.fire(EVENTS.UPDATE_AUTO_FONT_SIZE_VALUE, {
          tableId: tableInstance.id,
          autoFontSizeValue: null
        });
      }

      simpleTableBuilderEventBroker.fire(EVENTS.SAVE, {
        $element: this.$element,
        tableInstance,
        styles: this.styles,
        params: this.params,
        tableData: this.tableData
      });
    },
    // Shows what the table actually renders as, not what was requested: W and H are only
    // floors, so content can push the table past either of them. Pass syncWidth/syncHeight
    // false for the input being typed in, otherwise the measurement overwrites it.
    handleTableDimension({ syncWidth = true, syncHeight = true } = {}) {
      this.$nextTick(async () => {
        const tableDimension = await tableInstance.getDimension();

        if (syncWidth) {
          this.tableWidth = tableDimension.width;
          // The W box now holds a measured readout again, not what the user typed.
          this.userChangedTableWidth = false;
        }

        if (syncHeight) {
          this.tableHeight = tableDimension.height;
        }

        this.renderedColumnsWidth = tableDimension.columnsWidth || 0;
        this.naturalColumnsWidth = tableDimension.naturalColumnsWidth || 0;
        this.columnsSurplus = tableDimension.columnsSurplus || 0;
        this.tableOuterWidth = tableDimension.tableOuterWidth || 0;
        this.naturalTableWidth = tableDimension.naturalTableWidth || 0;
        this.columnOverflows = tableDimension.columnOverflows || [];
      });
    },
    async handleAutoHeightChange() {
      if (this.autoHeight) {
        this.autoFontSize = false;
        // Rotated text adds no height to the row on its own (a transform never affects
        // layout), so a content-driven table needs the fitted heights re-applied.
        fitTableCellsWithAngle(tableInstance, { force: true });
        tableInstance.setDimensions(this.widthToPersist(), 'auto');
        this.table = tableInstance.toData();
        // Re-measure so the H field reflects the new content-driven height.
        this.handleTableDimension();
      } else {
        // Remove the height from all rows to adjust the table based on the table's overall height.
        const cells = tableInstance.rows
          .reduce((acc, row) => [...acc, ...row.cells], [...tableInstance.headers])
          .map(cell => cell.id);

        tableInstance.changeRowStyles({
          cells,
          rules: [{
            rule: 'height',
            value: 0,
            measure: 'px'
          }],
        });

        // The wipe above dropped the fitted heights too — restore them before freezing
        // the table height, otherwise it is frozen too short and the table renders squashed.
        fitTableCellsWithAngle(tableInstance, { force: true });

        const tableDimension = await tableInstance.getDimension();

        // Freeze the natural height (tables render at their natural size).
        this.tableHeight = tableDimension.height;
        tableInstance.setDimensions(this.widthToPersist(), this.tableHeight);
        this.table = tableInstance.toData();
      }

    },
    async handleAdjustFontSizeToTable(value) {
      if (this.autoFontSize){
        this.autoHeight = false;
      }
    },
    onChangeBorderBoxWidths(value) {
      tableInstance.setBorderBoxWidths(value);

      this.table = tableInstance.toData();
      this.setColumnWidthsSum = tableInstance.getSumOfColumnWidths();
      // Every column changes what its width means, so the table is a different size now.
      this.handleTableDimension();
    },
    // Column Mode's rows come from the instance, so the toggle has to reach it before Save.
    onChangeHideHeaders(value) {
      tableInstance.setHeaderVisibility(!value);
      this.table = tableInstance.toData();
      this.setColumnWidthsSum = tableInstance.getSumOfColumnWidths();
      this.handleTableDimension();
    },
    async onChangeTableColumnMode(value) {
      tableInstance.setTableColumnMode(value);
      this.table = tableInstance.toData();
      // The on-screen columns differ between modes, so recompute the sum.
      this.setColumnWidthsSum = tableInstance.getSumOfColumnWidths();
      // Transposing changes the table's rendered size, so re-measure W/H.
      this.handleTableDimension();
    },
    handleChangeColumnWidth(rule, val) {
      const raw = val == null ? '' : String(val).trim();
      const value = raw.length ? raw : 'auto';
      const measure = raw.length ? 'px' : '';

      // "Fixed Width" / "Min Width" always change the on-screen horizontal size.
      // In Column Mode the on-screen column is the transposed visual column (for
      // a header that's all the headers), so target that instead of the stored
      // column.
      if (tableInstance.getTableColumnMode()) {
        this.selectedCells.forEach(cell => {
          tableInstance.applyColumnModeColumnStyle(cell.id, { [rule]: `${value}${measure}` });
        });

        this.table = tableInstance.toData();
        this.handleTableDimension();
      } else {
        this.handleChangeCellStyleRule(rule, value, measure);
      }

      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        this.setColumnWidthsSum = tableInstance.getSumOfColumnWidths();
      }, 300);
    },
    showFileUploadModal() {
      this.$refs.tableFileUploadModal.show();
    },
    handleAddImage(url) {
      tableInstance.setImage({
        cells: this.selectedCells.map(cell => cell.id),
        src: url
      });

      this.table = tableInstance.toData();
      this.selectedCellUrl = url;
      this.selectedCells[0].value = '';
      this.selectedCells[0].valueHtml = '';
    },
    copyColumnStyle() {
      this.columnStyles = tableInstance.getColumnStyles(this.selectedCells[0]);
    },

    applyStylesToColumn() {
      tableInstance.applyColumnStylesToColumn(this.selectedCells[0], this.columnStyles);
      this.columnStyles = [];
      this.table = tableInstance.toData();
    },
    applyTextStyle(style) {
      if (!style) return;
      style.list.forEach(attr => {
        if (attr.type === 'font') {
          const newFont = this.getFontObjectFromList(attr.value);
          if (!newFont) {
            toastr.warning('Font not found, please check theme Styles And Font settings');
            return;
          }
          this.fontObject = newFont;
          this.fontFamily = this.fontObject.index;
          this.handleChangeCellStyleRule('font-family', this.fontFamily, '');

        } else if (attr.type === 'number') {
          this.selectedCellTextSize = parseInt(attr.value) > 0 ? parseInt(attr.value) : 16;
          this.handleChangeCellStyleRule('font-size', this.selectedCellTextSize, 'px')
        } else if (attr.type === 'color') {
          this.selectedCellTextColor = attr.value || '#000';
          this.handleChangeCellStyleRule('color', this.selectedCellTextColor, '')
        }
      });
    },
    getStylePreview(style) {
      const font = _.get(style.list.find(attr => attr.type === 'font'), 'value') || 'inherit';
      const color = _.get(style.list.find(attr => attr.type === 'color'), 'value') || '#000';

      return {
        fontFamily: font,
        color: color
      };
    },

    matchSelectedStyle() {
      if (!this.themeStyles || this.themeStyles.length === 0) return;

      const matchedStyle = this.themeStyles.find(style => {
        const font = _.get(style.list.find(attr => attr.type === 'font'), 'value');
        const size = parseInt(_.get(style.list.find(attr => attr.type === 'number'), 'value'));
        const color = _.get(style.list.find(attr => attr.type === 'color'), 'value').toLowerCase();
        const matchesFont = font === this.fontFamily;
        const matchesSize = size === parseInt(this.selectedCellTextSize);
        const matchesColor = !color || color === this.selectedCellTextColor?.toLowerCase();

        return matchesFont && matchesSize && matchesColor;
      });

      if (matchedStyle) {
        this.selectedStyle = matchedStyle;
      } else {
        this.selectedStyle = null;
      }
    },
    handleCellBgColorTableChange(value, borderOptionSelected) {
      this.handleChangeCellStyleRule('cell-bg-table-color-value', value, '');
    },
    changeBorderOption() {
      this.selectedCellBorderColor = this.getCellBorderStyle(BORDER_STYLE_RULES.BORDER_COLOR, '#000000');
      this.selectedCellTcBorderColor = this.getCellBorderStyle(BORDER_STYLE_RULES.TC_BORDER_COLOR, TC_BORDER_COLOR_KEYS.DISABLED);
    },
    getCellBorderStyle(rule, defaultValue) {
      return this.getCellStyle(rule, defaultValue, this.applyToBorders);
    },
    getCellStyle(rule, defaultValue, applyTo = APPLY_TO_BORDERS_VALUES.ALL) {
      return tableInstance.getStyle(this.selectedCells[0].id, rule, defaultValue, applyTo);
    },

    // Inline cell editing methods
    onCellFocus(event, cell) {
      this.editingCellId = cell.id;
      this.originalCellValue = cell.valueHtml || '';
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.normalizeEditorTarget(event.target, true);

          const selectionNode = window.getSelection?.()?.anchorNode;
          this.setActiveTagContextFromNode(selectionNode, event.target);
        });
      });
    },

    onCellTextInput(event, cell) {
      // Prevent event from bubbling up to global handlers
      event.stopPropagation();

      // Don't update reactive data during typing to prevent cursor jumping
      // The text will be applied when the user finishes editing (on blur or enter)
    },

    sanitizeEditorHtml(html) {
      return DOMPurify.sanitize(String(html || ''), {
        ALLOWED_TAGS: [
          'b', 'strong', 'i', 'em', 'u',
          'br', 'p', 'span',
          'ul', 'ol', 'li',
          'sup', 'sub',
          'mark', 'small', 'img',
        ],
        ALLOWED_ATTR: ['style', 'src', 'width', 'height', 'alt'],
      });
    },

    getEditorHtml(target) {
      return this.sanitizeEditorHtml(target?.innerHTML || '');
    },

    normalizeEditorTarget(target, restoreCaret = false) {
      if (!target) return;

      const sanitizedHtml = this.getEditorHtml(target);
      if (sanitizedHtml === target.innerHTML) return;

      target.innerHTML = sanitizedHtml;

      if (!restoreCaret || document.activeElement !== target) return;

      const selection = window.getSelection?.();
      if (!selection) return;

      const range = document.createRange();
      range.selectNodeContents(target);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    },

    onCellTextBlur(event, cell) {
      const textHtml = this.getEditorHtml(event.target);
      this.applyCellTextChange(cell, textHtml);
      this.setActiveTagContextFromNode(event.target, event.target);
      this.editingCellId = null;
    },

    onCellKeyDown(event, cell) {
      // Prevent global keyboard shortcuts when editing cells
      if (['Backspace', 'Delete', 'Enter', 'Escape'].includes(event.key)) {
        event.stopPropagation(); // Prevent event from bubbling up to global handlers
      }

      // Handle Enter key to apply changes
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const newValue = this.getEditorHtml(event.target);
        this.applyCellTextChange(cell, newValue);
        event.target.blur();
        return;
      }

      // Handle Shift+Enter for line breaks (let it pass through)
      if (event.key === 'Enter' && event.shiftKey) {
        // Allow default behavior for line breaks
        return;
      }

      // Handle Escape to cancel editing
      if (event.key === 'Escape') {
        event.preventDefault();
        event.target.innerHTML = this.originalCellValue;
        event.target.blur();
        this.editingCellId = null;
        return;
      }

      // Handle Backspace and Delete - allow normal text editing behavior
      if (event.key === 'Backspace' || event.key === 'Delete') {
        // Let the default behavior handle text deletion within the cell
        // but prevent it from bubbling up to global handlers
        return;
      }
    },

    applyCellTextChange(cell, newValue) {
      const link = $('#link').val();
      // Blur commits even when nothing was typed — only a real change may re-fit, so that a
      // manually typed Height survives clicking in and out of the cell.
      const textChanged = tableInstance.getCellById(cell.id)?.valueHtml !== newValue;
      // Committed on blur like the link; without it every click away wiped the cell's name.
      const mappingName = $('#mapping-name').length ? $('#mapping-name').val() : undefined;
      // Update the cell value in the table instance
      tableInstance.changeCellValue({
        id: cell.id,
        value: newValue,
        valueHtml: newValue,
        link: link,
        mappingName,
        colspan: cell.colspan,
        rowspan: cell.rowspan,
      });

      if (textChanged) {
        this.refitCellAngle(cell.id);
      }

      // Refresh the table data
      this.table = tableInstance.toData();

      // Update selected cells if this cell is currently selected
      const selectedCellIndex = this.selectedCells.findIndex(selectedCell => selectedCell.id === cell.id);
      if (selectedCellIndex >= 0) {
        this.selectedCells[selectedCellIndex].value = newValue;
        this.selectedCells[selectedCellIndex].valueHtml = newValue;
        this.selectedCells[selectedCellIndex].link = link;
        if (mappingName !== undefined) this.selectedCells[selectedCellIndex].mappingName = mappingName;
      }
    },

    // The stored fit was measured for the text that has just been replaced, and rotated text is
    // out of flow — it cannot grow its row on its own, so it has to be re-measured here.
    refitCellAngle(id) {
      const cell = tableInstance.getCellById(id);
      const size = cell ? measureCellAngleFit(cell, tableInstance.styles) : null;

      if (!size) return;

      tableInstance.applyAngleFit(id, size);

      this.handleTableDimension();

      if (this.selectedCells[0]?.id !== id) return;

      this.selectedCellMinHeight = this.tableColumnMode
        ? this.getColumnModeOnScreenRowHeight(id)
        : this.getCellStyle('height', 0);
      this.selectedCellMinWidth = size.width;
    },

    // Helper methods for insert/move functionality
    getRowIndex(cellId) {
      if (!this.table) return -1;

      if (Array.isArray(this.table.staticRows)) {
        if (this.table.staticRows.some(cell => cell.id === cellId)) {
          return -1;
        }
      }

      // Check if it's a header cell (static headers)
      if (this.table.staticHeaders) {
        const headerIndex = this.table.staticHeaders.findIndex(cell => cell.id === cellId);
        if (headerIndex >= 0) {
          return -1; // Headers are at position -1 (before all rows)
        }
      }

      // Check if it's a regular header cell
      if (this.table.headers) {
        const headerIndex = this.table.headers.findIndex(cell => cell.id === cellId);
        if (headerIndex >= 0) {
          return -1; // Headers are at position -1 (before all rows)
        }
      }

      if (this.table.staticFooters) {
        const footerIndex = this.table.staticFooters.findIndex(cell => cell.id === cellId);
        if (footerIndex >= 0) {
          return -1;
        }
      }

      // Check body rows
      if (this.table.rows) {
        for (let i = 0; i < this.table.rows.length; i++) {
          const row = this.table.rows[i];
          if (row.cells && row.cells.some(cell => cell.id === cellId)) {
            return i;
          }
        }
      }
      return -1;
    },

    insertRowAbove() {
      if (this.canInsertRowAbove) this.insertOnScreen('row', 0);
    },

    insertRowBelow() {
      if (this.canInsertRowBelow) this.insertOnScreen('row', 1);
    },

    insertColumnLeft() {
      if (this.canInsertColumnLeft) this.insertOnScreen('column', 0);
    },

    insertColumnRight() {
      if (this.canInsertColumnRight) this.insertOnScreen('column', 1);
    },

    // Column Mode renders the table transposed: an on-screen row is a stored column and back.
    insertOnScreen(line, offset) {
      const { row, column } = this.selectedCellPosition;
      const selectedCellId = this.selectedCells[0].id;

      const transposed = this.table.tableColumnMode;
      const storedLine = transposed ? { row: 'column', column: 'row' }[line] : line;
      const storedIndex = (line === 'row' ? row : column - this.firstBodyColumn) + offset;

      if (storedLine === 'row') {
        tableInstance.addNewRow(storedIndex);
      } else {
        tableInstance.addNewColumn(storedIndex);
      }

      this.table = tableInstance.toData();
      this.$nextTick(() => this.maintainCellSelection(selectedCellId));
    },

    // Helper method to maintain cell selection after table structure changes
    maintainCellSelection(cellId) {
      // Find the cell in the updated table structure
      let foundCell = null;

      // Check headers first
      if (Array.isArray(this.table.staticRows)) {
        foundCell = this.table.staticRows.find(cell => cell.id === cellId);
      }

      if (!foundCell && this.table.staticHeaders) {
        foundCell = this.table.staticHeaders.find(cell => cell.id === cellId);
      }

      if (!foundCell && this.table.staticFooters) {
        foundCell = this.table.staticFooters.find(cell => cell.id === cellId);
      }

      // Check regular headers
      if (!foundCell && this.table.headers) {
        foundCell = this.table.headers.find(cell => cell.id === cellId);
      }

      // Check body cells
      if (!foundCell && this.table.rows) {
        for (const row of this.table.rows) {
          if (row.cells) {
            foundCell = row.cells.find(cell => cell.id === cellId);
            if (foundCell) break;
          }
        }
      }

      // If cell was found, maintain selection
      if (foundCell) {
        this.selectedCells = [{
          id: foundCell.id,
          value: foundCell.value,
          valueHtml: foundCell.valueHtml,
          colspan: foundCell.colspan,
          rowspan: foundCell.rowspan,
          dotLeaderEnabled: foundCell.dotLeaderEnabled,
          mappingName: foundCell.mappingName,
        }];
        this.selectedCellDotLeaderEnabled = !!foundCell.dotLeaderEnabled;
      }
    },

    moveRowUp() {
      if (this.canMoveRowUp) this.moveOnScreen('up');
    },

    moveRowDown() {
      if (this.canMoveRowDown) this.moveOnScreen('down');
    },

    moveColumnLeft() {
      if (this.canMoveColumnLeft) this.moveOnScreen('left');
    },

    moveColumnRight() {
      if (this.canMoveColumnRight) this.moveOnScreen('right');
    },

    moveOnScreen(direction) {
      const transposed = { up: 'left', down: 'right', left: 'up', right: 'down' };
      const stored = this.table.tableColumnMode ? transposed[direction] : direction;

      if (stored === 'up' || stored === 'down') {
        tableInstance.moveRow(stored, this.selectedCells[0]);
      } else {
        tableInstance.moveColumn(stored, this.selectedCells[0]);
      }
      this.table = tableInstance.toData();
    }
  }
};
</script>

<style lang="scss" scoped>

.table-builder-container {
  overflow: auto;
  min-height: 500px;
  max-height: 500px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;

  .content-editor {
    cursor: text;
    width: 100%;
    height: 100%;
  }

  // Containing block for rotated cell text, which is out of flow so it cannot force
  // the column to the width of the unrotated text. The rendered table gets this from
  // TableCell.toHTML; here the cell styles come from stringStyles instead.
  td {
    position: relative;
  }
}

.table-builder-container--fullscreen {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
}

// overflow: hidden clips to the scaled size — a transform alone doesn't shrink the layout box.
.table-zoom-stage {
  position: relative;
  overflow: hidden;
}

// inline-block avoids squeezing to the stage's scaled width; vertical-align: top avoids its baseline gap.
.table-zoom-source {
  display: inline-block;
  vertical-align: top;
}

// ::v-deep because dropdown toggles get this class from b-dropdown, outside this component's scope.
::v-deep .builder-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fafafa;
  color: #0f172a;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  box-shadow: none;

  &:not(:disabled):hover {
    background: #f1f5f9;
    color: #0f172a;
    text-decoration: none;
  }

  &:focus {
    text-decoration: none;
    box-shadow: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

::v-deep .builder-btn--ghost {
  border-color: transparent;
  background: transparent;
  font-size: 14px;
}

::v-deep .builder-btn--icon {
  width: 28px;
  padding: 0;
  justify-content: center;
}

::v-deep .builder-btn--primary {
  border-color: #189a73;
  background: #189a73;
  color: #fff;

  &:not(:disabled):hover {
    border-color: #137a5b;
    background: #137a5b;
    color: #fff;
  }
}

::v-deep .builder-btn--lg {
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
}

.builder-divider {
  flex-shrink: 0;
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: #e2e8f0;
}

.builder-label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #64748b;
}

.table-builder-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.table-toolbar {
  gap: 6px;
  margin: 0 -24px;
  padding: 10px 24px;
  border-bottom: 1px solid #e2e8f0;

  &--main {
    margin-top: -16px;
    padding-block: 12px;
    background: #f6f8fa;
  }

  &__swatch {
    margin-left: 4px;
  }

  &__size-input {
    width: 80px;
    height: 28px;
    padding: 0 8px;
    font-size: 13px;
  }

  &__switch {
    max-width: 120px;
    font-size: 12px;
    line-height: 16px;
    color: #64748b;
  }
}

.table-toolbar,
.table-cell-panel {
  .form-control,
  .custom-select {
    border-color: #e2e8f0;
    border-radius: 6px;
    background-color: #fafafa;
    color: #0f172a;
    font-size: 13px;

    &:focus {
      border-color: #189a73;
      box-shadow: 0 0 0 2px rgba(24, 154, 115, 0.15);
    }

    &:disabled {
      background-color: #f1f5f9;
      color: #94a3b8;
    }
  }
}

.table-toolbar__swatch,
.table-cell-panel__swatch {
  display: flex;

  ::v-deep .pcr-button {
    display: block;
    border-radius: 6px;

    &::before,
    &::after {
      border: 1px solid #e2e8f0;
      border-radius: 6px;
    }
  }
}

.table-toolbar__swatch ::v-deep .pcr-button {
  width: 26px;
  height: 26px;
}

.table-cell-panel__swatch ::v-deep .pcr-button {
  width: 40px;
  height: 30px;
}

.builder-segmented ::v-deep {
  .btn {
    display: inline-flex;
    align-items: center;
    height: 30px;
    padding: 0 9px;
    border-color: #e2e8f0;
    background: #fafafa;
    color: #0f172a;
    font-size: 12px;
    font-weight: 400;
    box-shadow: none;
  }

  .btn:not(:disabled):not(.disabled):hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  .btn:not(:disabled):not(.disabled).active {
    border-color: #189a73;
    background: #189a73;
    color: #fff;
  }

  .btn.disabled {
    color: #9ca3af;
    opacity: 1;
  }

  .btn:focus,
  .btn.focus,
  .btn:not(:disabled):not(.disabled).active:focus {
    box-shadow: none;
  }
}

.table-cell-panel {
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;

  &__side {
    flex: 0 0 220px;
    gap: 12px;
    min-width: 0;

    input.form-control {
      height: 32px;
    }
  }

  &__name-label {
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
  }

  &__mapping-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
  }

  &__controls {
    flex: 1 1 auto;
    min-width: 0;
    padding: 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fafafa;
  }

  &__row {
    gap: 8px;
  }

  &__font-family {
    width: 160px;
  }

  &__align {
    width: 128px;
  }

  &__font-size {
    width: 48px;
  }

  &__number {
    width: 70px;
  }

  &__angle {
    width: 48px;
  }

  &__fit {
    flex-shrink: 0;
    height: 31px;
  }

  &__dots {
    margin-bottom: 5px;
  }

  &__borders {
    gap: 8px;
    margin-top: 16px;
  }

  &__border-width {
    width: 80px;
  }

  &__border-style {
    width: 104px;
  }

  &__apply {
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;

    .builder-segmented ::v-deep .btn {
      height: 28px;
    }
  }

  &__span {
    flex: 0 0 200px;
    min-width: 0;
  }

  &__span-input {
    width: 56px;
  }

  &__hint {
    margin-top: 12px;
    font-size: 12px;
    line-height: 16px;
    color: #64748b;

    & + & {
      margin-top: 8px;
    }

    &--danger {
      color: #ef4444;
    }
  }
}

.table-zoom-control {
  gap: 4px;
  height: 28px;
  padding: 0 6px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fafafa;

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: #050b19;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.35;
    }

    &:not(:disabled):hover {
      background: #f0f2f5;
      border-radius: 3px;
    }
  }

  &__value {
    width: 40px;
    text-align: center;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: #334155;
  }
}

.selected-cell {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    border-color: #2eb85c;
    border-style: solid;
    pointer-events: none;
  }
}

.editing-cell {
  // No background override here. It used to force #fff !important, which beats the cell's own
  // inline background — so clicking into a green header turned it white, and because those cells
  // have white text you could not see what you were typing. The blue border is the affordance.
  border: 2px solid #007bff !important;
  outline: none !important;
}

td {
  position: relative;
  cursor: text;

  &:hover {
    background-color: #f8f9fa;
  }
}

.cell-content-shell {
  display: grid;
}

.cell-content-shell > * {
  grid-area: 1 / 1;
}

.cell-display {
  pointer-events: none;
}

.cell-editor {
  position: relative;
  z-index: 1;
  min-height: 20px;
  width: 100%;
  outline: none;
  // Transparent, not white: this layer sits on top of the td, so a colour here paints over the
  // cell's own background the moment editing starts — a green header went white on click. While
  // editing the display layer underneath is gone (v-if), so there is nothing to mask anyway.
  background-color: transparent;
  caret-color: currentColor;
}

// Out of flow while it is not being edited: the editor holds the cell's text unrotated, so in
// flow it would keep a rotated cell's column as wide as that text — the rotated content itself
// is out of flow precisely to avoid that. A cell that is not rotated is unaffected, since its
// text sits in the display layer too.
.cell-editor-inactive {
  opacity: 0;
  position: absolute;
  inset: 0;
}

.no-select {
  user-select: none;
}

.table-multiselect {
  min-height: 32px;
  font-size: 13px;

  ::v-deep .multiselect__tags {
    min-height: 32px;
    padding: 5px 40px 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #fafafa;
    font-size: 13px;
  }

  ::v-deep .multiselect__select {
    height: 30px;
  }

  // The search input replaces the placeholder while open, so all three share one box or the field jumps.
  ::v-deep .multiselect__placeholder,
  ::v-deep .multiselect__single,
  ::v-deep .multiselect__input {
    min-height: 20px;
    margin-bottom: 5px;
    padding: 0;
    border-radius: 0;
    background: transparent;
    font-size: 13px;
    line-height: 20px;
  }

  ::v-deep .multiselect__content-wrapper {
    border-color: #e2e8f0;
    background: #fff;
  }

  ::v-deep .multiselect__option {
    min-height: 32px;
    padding: 8px 12px;
    font-size: 13px;
    color: #0f172a;
  }

  ::v-deep .multiselect__option--highlight,
  ::v-deep .multiselect__option--selected.multiselect__option--highlight {
    background: #f1f5f9;
    color: #0f172a;
  }

  ::v-deep .multiselect__option--selected {
    background: transparent;
    font-weight: 600;
  }
}

.format-hover-dropdown {
  position: relative;
  display: inline-block;

  &__menu {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 1000;
    min-width: 180px;
    padding: 4px 0;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 0.25rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }

  &__item {
    display: block;
    width: 100%;
    padding: 0.25rem 1rem;
    border: 0;
    background: transparent;
    text-align: left;
    color: #212529;

    &:hover,
    &:active,
    &.active {
      background: #e9ecef;
    }
  }
}
</style>

<!--
  Global (non-scoped) so it reaches BootstrapVue's `.modal-dialog`, which is
  rendered outside this component's scoped subtree. Keeps the Table Builder a
  consistent width whether it's opened from the backend template editor or the
  frontend show page (otherwise it falls back to `.modal-xl`, which differs
  between the admin/CoreUI and frontend stylesheets).
-->
<style lang="scss">
.table-options-menu {
  white-space: nowrap;
}

// toastr pins #toast-container to the viewport; inside the modal it has to follow the popup.
#toast-container.toast-table-builder {
  position: absolute;
  top: 14px;
  right: 104px;
}

#toast-container.toast-table-builder > div {
  opacity: 1;
}

.table-create-menu {
  width: 180px;
}

#simple-table-builder input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

#simple-table-builder input[type="number"]::-webkit-outer-spin-button,
#simple-table-builder input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.modal-1200 .modal-dialog {
  width: 1200px !important;
  max-width: 1200px !important;
}

.table-builder-fullscreen .modal-dialog {
  width: 100vw !important;
  max-width: 100vw !important;
  height: 100vh;
  margin: 0;
}

// Bootstrap's centering spacer is a min-content sibling of the content — with a
// full-height content box it would push the modal past the viewport.
.table-builder-fullscreen .modal-dialog-centered::before {
  display: none;
}

.table-builder-fullscreen .modal-content {
  height: 100vh;
  border-radius: 0;
}

// Flex column so the table area can claim the leftover height instead of the whole
// body scrolling, which would push the toolbar out of view while editing cells.
.table-builder-fullscreen .modal-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

#simple-table-builder .modal-content {
  border-color: #e2e8f0;
  background: #fafafa;
}

#simple-table-builder .modal-header {
  align-items: center;
  padding: 16px 24px;
  border-bottom-color: #e2e8f0;
}

#simple-table-builder .modal-body {
  padding: 16px 24px;
}

#simple-table-builder .modal-footer {
  padding: 16px 24px;
  border-top-color: #e2e8f0;
}
</style>
